import React, { useEffect, useRef, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, API_BASE, apiError } from "@/lib/api";
import { LetterheadStudio } from "@/pages/lab/LetterheadStudio";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useSiteSettings } from "@/lib/settings";
import { toast } from "sonner";
import { Palette, Upload, Loader2, Award, PenLine, Trash2, Plus, Star, FileImage, LayoutTemplate, ShieldCheck, Gift, RotateCcw, Frame as FrameIcon, CalendarDays, MessageCircle, ExternalLink, Globe, EyeOff, Lock, AlignLeft, AlignCenter, AlignRight, Image as ImageIcon, Sparkles } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import LetterheadLivePreview from "./LetterheadLivePreview";
import AdvancedLetterheadCard from "./AdvancedLetterheadCard";

function ReferralProgramCard() {
  const [state, setState] = useState({ effective: {}, override: {}, platform_default: {} });
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/lab/referral-program");
      setState(r.data);
      // Prefer override values; fall back to effective for the form.
      setForm({ ...r.data.effective, ...r.data.override });
    } catch (e) { toast.error(apiError(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/lab/referral-program", form);
      toast.success("Referral program updated");
      await load();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset to platform defaults? Your lab's override will be removed.")) return;
    try {
      await api.delete("/lab/referral-program");
      toast.success("Reverted to platform defaults");
      await load();
    } catch (e) { toast.error(apiError(e, "Reset failed")); }
  };

  const hasOverride = Object.keys(state.override || {}).length > 0;

  return (
    <Card className="mt-6 p-6 rounded-2xl" data-testid="referral-program-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><Gift className="w-5 h-5 text-efgh-orange" /> Referral rewards</div>
          <p className="text-sm text-ink/60">Customise your lab&apos;s "Refer & Earn" program. Overrides the platform default when set.</p>
        </div>
        <div className="flex gap-2">
          {hasOverride && (
            <Button type="button" variant="outline" onClick={reset} data-testid="referral-reset">
              <RotateCcw className="w-4 h-4 mr-2" /> Use platform default
            </Button>
          )}
          <Button type="button" onClick={save} disabled={saving} data-testid="save-referral-program" className="bg-ink hover:bg-ink/90 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />}Save program
          </Button>
        </div>
      </div>

      {loading ? <div className="mt-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-ink/40 mx-auto" /></div> : (
        <div className="mt-5 space-y-4">
          <label className="flex items-center gap-3 p-3 rounded-xl border border-border">
            <input type="checkbox" data-testid="referral-enabled" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4" />
            <div className="flex-1">
              <div className="font-medium text-ink text-sm">Enable referral program for my lab&apos;s patients</div>
              <div className="text-xs text-ink/50">Toggle off to hide the "Refer & Earn" section for patients whose latest lab is yours.</div>
            </div>
          </label>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 rounded-xl border border-border">
              <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold">Referrer reward (existing patient)</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label>Percent off</Label>
                  <Input type="number" min={0} max={100} step={0.5} data-testid="referrer-percent" value={form.referrer_percent ?? ""} onChange={(e) => setForm({ ...form, referrer_percent: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="0" />
                </div>
                <div>
                  <Label>Flat ₹ off</Label>
                  <Input type="number" min={0} step={1} data-testid="referrer-flat" value={form.referrer_flat ?? ""} onChange={(e) => setForm({ ...form, referrer_flat: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="0" />
                </div>
              </div>
            </div>
            <div className="p-4 rounded-xl border border-border">
              <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold">Referee reward (new patient)</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label>Percent off</Label>
                  <Input type="number" min={0} max={100} step={0.5} data-testid="referee-percent" value={form.referee_percent ?? ""} onChange={(e) => setForm({ ...form, referee_percent: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="0" />
                </div>
                <div>
                  <Label>Flat ₹ off</Label>
                  <Input type="number" min={0} step={1} data-testid="referee-flat" value={form.referee_flat ?? ""} onChange={(e) => setForm({ ...form, referee_flat: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="0" />
                </div>
              </div>
            </div>
          </div>

          <div>
            <Label>Program description</Label>
            <Input data-testid="referral-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Refer a friend and both get 10% off your next test." />
          </div>
          <div>
            <Label>Terms &amp; conditions (optional)</Label>
            <Textarea data-testid="referral-terms" rows={2} value={form.terms || ""} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="Reward applies to your next booking. One reward per referred friend. Only new patients qualify." />
          </div>

          {!hasOverride && (
            <div className="p-3 rounded-xl bg-efgh-yellow/10 border border-efgh-yellow/30 text-xs text-ink/70">
              Currently using EFGH platform defaults. Edit any field &amp; Save to activate a lab-specific override.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ReviewersCard() {
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/lab/reviewers").then((r) => setUsers(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = (uid) => setUsers((prev) => prev.map((u) => u.id === uid ? { ...u, is_authorized_reviewer: !u.is_authorized_reviewer } : u));
  const save = async () => {
    setSaving(true);
    try {
      const ids = users.filter((u) => u.is_authorized_reviewer).map((u) => u.id);
      await api.put("/lab/reviewers", { authorized_reviewer_ids: ids });
      toast.success(`${ids.length} authorised reviewer${ids.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };
  const anyChecked = users.some((u) => u.is_authorized_reviewer);
  return (
    <Card className="mt-6 p-6 rounded-2xl" data-testid="reviewers-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-efgh-orange" /> Authorised reviewers</div>
          <p className="text-sm text-ink/60">Only ticked lab members can publish reports to patients. Untick all to let every lab admin publish (default).</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-reviewers" className="bg-ink hover:bg-ink/90 text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}Save reviewers
        </Button>
      </div>
      {users.length === 0 ? (
        <div className="mt-4 py-6 text-center text-sm text-ink/40 border border-dashed rounded-xl">No lab admins found in your team yet.</div>
      ) : (
        <div className="mt-4 space-y-2">
          {users.map((u) => (
            <label key={u.id} data-testid={`reviewer-row-${u.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer hover:bg-muted/40">
              <input type="checkbox" className="w-4 h-4" checked={!!u.is_authorized_reviewer} onChange={() => toggle(u.id)} data-testid={`reviewer-chk-${u.id}`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-ink text-sm truncate">{u.full_name || u.email}</div>
                <div className="text-xs text-ink/50 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
              </div>
              {u.is_authorized_reviewer && <span className="pill pill-normal text-[10px]">Reviewer</span>}
            </label>
          ))}
        </div>
      )}
      <div className="mt-3 text-xs text-ink/50">
        {anyChecked ? "Only the ticked users will see the Publish button on reports." : "No one is explicitly ticked — every lab admin can publish (open mode)."}
      </div>
    </Card>
  );
}

export default function LabBranding({ mode = "branding" }) {
  const { user } = useAuth();
  const [lab, setLab] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ name: "", slug: "", email: "", phone: "", address: "", logo_url: "", primary_color: "#00897B", secondary_color: "#0B1628", website: "" });

  useEffect(() => {
    if (user?.lab_id) {
      api.get(`/labs/${user.lab_id}`).then((r) => { setLab(r.data); setForm(r.data); });
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    // Prefer the loaded lab's id (guaranteed fresh from DB) over user.lab_id (possibly stale JWT payload)
    const labId = lab?.id || user?.lab_id;
    if (!labId) { toast.error("Your account isn't linked to a lab yet — please sign out and sign in again."); return; }
    try {
      const r = await api.put(`/labs/${labId}`, form);
      setLab(r.data);
      toast.success("Branding saved");
    } catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API_BASE}/files/${r.data.path}`;
      setForm((prev) => ({ ...prev, logo_url: url }));
      toast.success("Logo uploaded — save branding to apply");
    } catch (err) {
      toast.error(apiError(err, "Logo upload failed"));
    } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  if (!user?.lab_id) return <DashboardLayout role="lab_admin"><Card className="p-8">No lab attached to your account.</Card></DashboardLayout>;

  const uploadAsset = async (file, kind) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) { toast.error(`${kind} must be under 5 MB`); return null; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return `${API_BASE}/files/${r.data.path}`;
    } catch (err) { toast.error(apiError(err, `${kind} upload failed`)); return null; }
  };

  // Persist a patched `form` immediately and refresh lab state. Used by asset add/remove
  // handlers so the Assets tab does not need its own Save button.
  const persistAssets = async (nextForm, successMsg) => {
    const labId = lab?.id || user?.lab_id;
    if (!labId) { toast.error("Your account isn't linked to a lab yet — please sign out and sign in again."); return; }
    setForm(nextForm);
    try {
      const r = await api.put(`/labs/${labId}`, nextForm);
      setLab(r.data);
      if (successMsg) toast.success(successMsg);
    } catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  const addAccreditation = async (file) => {
    const url = await uploadAsset(file, "Accreditation logo");
    if (!url) return;
    const label = prompt("Accreditation label (e.g. NABL, CAP, ISO 9001):")?.trim();
    if (!label) return;
    const next = { ...form, accreditation_logos: [...(form.accreditation_logos || []), { label, logo_url: url }] };
    await persistAssets(next, `${label} logo added`);
  };

  const removeAccreditation = async (i) => {
    const next = { ...form, accreditation_logos: (form.accreditation_logos || []).filter((_, idx) => idx !== i) };
    await persistAssets(next, "Accreditation removed");
  };

  // Patch a single asset item (accreditation / custom_logo / signature).
  // Used by inline height sliders to independently size each image.
  const patchAssetItem = async (kind, i, patch) => {
    const key = { accreditation: "accreditation_logos", custom: "custom_logos", signature: "doctor_signatures" }[kind];
    if (!key) return;
    const arr = [...(form[key] || [])];
    if (!arr[i]) return;
    arr[i] = { ...arr[i], ...patch };
    const next = { ...form, [key]: arr };
    await persistAssets(next, null);
  };


  const addCustomLogo = async (file) => {
    const url = await uploadAsset(file, "Custom logo");
    if (!url) return;
    const label = prompt("Short label for this logo (e.g. 'Partner - Apollo', 'Award 2024'):")?.trim();
    if (!label) return;
    const item = {
      id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `cl-${Date.now()}`,
      label,
      logo_url: url,
    };
    const next = { ...form, custom_logos: [...(form.custom_logos || []), item] };
    await persistAssets(next, `${label} added`);
  };

  const removeCustomLogo = async (i) => {
    const next = { ...form, custom_logos: (form.custom_logos || []).filter((_, idx) => idx !== i) };
    await persistAssets(next, "Logo removed");
  };

  const addSignature = async (file) => {
    const url = await uploadAsset(file, "Signature");
    if (!url) return;
    const name = prompt("Doctor's name:")?.trim();
    if (!name) return;
    const qualification = prompt("Qualification (e.g. MD, Pathology):")?.trim() || "";
    const registration_no = prompt("Registration number (optional):")?.trim() || "";
    const sig = {
      id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `sig-${Date.now()}`,
      name, qualification, registration_no, signature_url: url,
      is_default: (form.doctor_signatures || []).length === 0,
    };
    const next = { ...form, doctor_signatures: [...(form.doctor_signatures || []), sig] };
    await persistAssets(next, `Dr. ${name} added`);
  };

  const removeSignature = async (i) => {
    const next = { ...form, doctor_signatures: (form.doctor_signatures || []).filter((_, idx) => idx !== i) };
    await persistAssets(next, "Signature removed");
  };

  const setDefaultSignature = async (i) => {
    const next = {
      ...form,
      doctor_signatures: (form.doctor_signatures || []).map((s, idx) => ({ ...s, is_default: idx === i })),
    };
    await persistAssets(next, "Default signature updated");
  };

  const onCustomLetterheadFile = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Letterhead must be under 4 MB"); return; }
    const url = await uploadAsset(file, "Letterhead");
    if (!url) return;
    const next = { ...form, custom_letterhead_url: url, letterhead_mode: "custom" };
    setForm(next);
    // Persist immediately so the mode doesn't silently revert on refresh.
    try {
      const r = await api.put(`/labs/${lab?.id || user.lab_id}`, next);
      setLab(r.data);
      toast.success("Custom letterhead saved");
    } catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  const saveLetterheadMode = async (mode) => {
    const next = { ...form, letterhead_mode: mode };
    if (mode === "standard") next.custom_letterhead_url = null;
    setForm(next);
    try {
      const r = await api.put(`/labs/${lab?.id || user.lab_id}`, next);
      setLab(r.data);
      toast.success(mode === "custom" ? "Switched to custom letterhead" : "Using EFGH standard letterhead");
    } catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  const removeCustomLetterhead = async () => {
    const next = { ...form, custom_letterhead_url: null, letterhead_mode: "standard" };
    setForm(next);
    try {
      const r = await api.put(`/labs/${lab?.id || user.lab_id}`, next);
      setLab(r.data);
      toast.success("Custom letterhead removed");
    } catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  const saveContentMargins = async ({ top, bottom }) => {
    const next = {
      ...form,
      letterhead_content_top_mm: Math.max(0, Math.min(120, top ?? form.letterhead_content_top_mm ?? 40)),
      letterhead_content_bottom_mm: Math.max(0, Math.min(80, bottom ?? form.letterhead_content_bottom_mm ?? 20)),
    };
    setForm(next);
    try {
      const r = await api.put(`/labs/${lab?.id || user.lab_id}`, next);
      setLab(r.data);
      toast.success("Letterhead margins saved");
    } catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  return (
    <DashboardLayout role="lab_admin">
      <BrandingLayout
        mode={mode}
        form={form}
        setForm={setForm}
        lab={lab}
        setLab={setLab}
        user={user}
        uploading={uploading}
        fileInputRef={fileInputRef}
        onLogoFile={onLogoFile}
        save={save}
        addAccreditation={addAccreditation}
        removeAccreditation={removeAccreditation}
        addCustomLogo={addCustomLogo}
        removeCustomLogo={removeCustomLogo}
        addSignature={addSignature}
        removeSignature={removeSignature}
        setDefaultSignature={setDefaultSignature}
        patchAssetItem={patchAssetItem}
      />
    </DashboardLayout>
  );
}

/* ============================================================================
 * BrandingLayout — new tabbed UI with sticky live preview on desktop.
 * Tabs: Identity · Letterhead · Layout · Field Styles · Assets · Team & Programs
 * ========================================================================== */
function BrandingLayout({ mode = "branding", form, setForm, lab, setLab, user, uploading, fileInputRef, onLogoFile, save, addAccreditation, removeAccreditation, addCustomLogo, removeCustomLogo, addSignature, removeSignature, setDefaultSignature, patchAssetItem }) {
  // Settings now includes the letterhead studio as the 2nd tab (right after Identity)
  // and no longer surfaces a separate Assets tab — those live inside the studio now.
  const SETTINGS_TAB_IDS = ["identity", "studio", "booking", "programs", "language"];
  // Branding mode now shows the unified LetterheadStudio (no separate letterhead/layout/field-styles tabs).
  const BRANDING_TAB_IDS = ["studio"];
  const activeIds = mode === "settings" ? SETTINGS_TAB_IDS : BRANDING_TAB_IDS;
  // Honor ?tab=xxx in the URL so external links can deep-link to a specific tab.
  const urlTab = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tab")) || "";
  const initialTab = activeIds.includes(urlTab) ? urlTab : activeIds[0];
  const [tab, setTab] = useState(initialTab);
  const [previewOpen, setPreviewOpen] = useState(false); // mobile drawer

  const ALL_TABS = [
    { id: "identity",    label: "Identity",   icon: Palette,         desc: "Name, logo, colours, contact" },
    { id: "booking",     label: "Booking",    icon: CalendarDays,    desc: "How patients book follow-up tests" },
    { id: "studio",      label: "Letterhead studio", icon: Sparkles, desc: "Design your letterhead — one canvas, live preview" },
    { id: "letterhead",  label: "Letterhead", icon: LayoutTemplate,  desc: "Header/footer layout & typography" },
    { id: "layout",      label: "Layout",     icon: FrameIcon,       desc: "Margins, gaps, borders, watermark" },
    { id: "field-styles",label: "Field styles", icon: PenLine,       desc: "Per-field font/size/colour overrides" },
    { id: "assets",      label: "Assets",     icon: Award,           desc: "Logos, accreditations, signatures" },
    { id: "programs",    label: "Team & Programs", icon: ShieldCheck, desc: "Reviewers, referrals, marketplace" },
    { id: "language",    label: "Language",    icon: Globe,           desc: "Regional audio & translated summary" },
  ];
  const TABS = ALL_TABS.filter((t) => activeIds.includes(t.id));

  const putLetterhead = async (nextLetterhead, msg) => {
    const next = { ...form, letterhead: nextLetterhead };
    setForm(next);
    try { const r = await api.put(`/labs/${lab?.id || user.lab_id}`, next); setLab(r.data); toast.success(msg || "Saved"); }
    catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  return (
    <>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          {mode === "settings" ? (
            <>
              <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-brand" /> Settings</h1>
              <p className="text-ink/60 text-sm mt-1">Laboratory identity, booking, assets, team & programs.</p>
            </>
          ) : (
            <>
              <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Palette className="w-6 h-6 text-brand" /> Branding</h1>
              <p className="text-ink/60 text-sm mt-1">Design your white-labeled smart-report letterhead. Preview updates in real time.</p>
            </>
          )}
        </div>
        {mode !== "settings" && (
          <button type="button" onClick={() => setPreviewOpen(true)} data-testid="mobile-preview-toggle" className="lg:hidden inline-flex items-center gap-2 h-10 px-4 rounded-md bg-ink text-white text-sm font-medium">
            <FileImage className="w-4 h-4" /> Show preview
          </button>
        )}
      </div>

      <div className={`mt-6 grid ${mode === "settings" || tab === "studio" ? "" : "lg:grid-cols-[minmax(0,1fr)_460px]"} gap-6`}>
        {/* ============ LEFT: Sub-nav + panels ============ */}
        <div className="min-w-0">
          <SubNav tabs={TABS} value={tab} onChange={setTab} />

          <div className="mt-4">
            {tab === "identity" && (
              <IdentityPanel form={form} setForm={setForm} save={save} uploading={uploading} fileInputRef={fileInputRef} onLogoFile={onLogoFile} onSave={async (patch) => {
                const next = { ...form, ...patch }; setForm(next);
                try { const r = await api.put(`/labs/${lab?.id || user.lab_id}`, next); setLab(r.data); toast.success("Contact details saved"); }
                catch (err) { toast.error(apiError(err, "Save failed")); }
              }} />
            )}
            {tab === "booking" && (
              <BookingSettingsPanel form={form} setForm={setForm} labId={lab?.id || user.lab_id} onSave={async (patch) => {
                const next = { ...form, ...patch }; setForm(next);
                try { const r = await api.put(`/labs/${lab?.id || user.lab_id}`, next); setLab(r.data); toast.success("Booking settings saved"); }
                catch (err) { toast.error(apiError(err, "Save failed")); }
              }} />
            )}
            {tab === "studio" && (
              <LetterheadStudio
                lab={lab}
                form={form}
                setForm={setForm}
                save={() => putLetterhead(form.letterhead, "Letterhead saved")}
                addAccreditation={addAccreditation}
                removeAccreditation={removeAccreditation}
                addCustomLogo={addCustomLogo}
                removeCustomLogo={removeCustomLogo}
                addSignature={addSignature}
                removeSignature={removeSignature}
                setDefaultSignature={setDefaultSignature}
                patchAssetItem={patchAssetItem}
              />
            )}
            {tab === "letterhead" && (
              <LetterheadDesignCard form={form} setForm={setForm} onSave={(lh) => putLetterhead(lh, "Letterhead saved")} />
            )}
            {tab === "layout" && (
              <AdvancedLetterheadCard form={form} setForm={setForm} onSave={(lh) => putLetterhead(lh, "Advanced layout saved")} />
            )}
            {tab === "field-styles" && (
              <FieldStylesCard form={form} setForm={setForm} onSave={(lh) => putLetterhead(lh, "Field styles saved")} />
            )}
            {tab === "assets" && (
              <AssetsPanel form={form} setForm={setForm} lab={lab} addAccreditation={addAccreditation} removeAccreditation={removeAccreditation} addCustomLogo={addCustomLogo} removeCustomLogo={removeCustomLogo} addSignature={addSignature} removeSignature={removeSignature} setDefaultSignature={setDefaultSignature} patchAssetItem={patchAssetItem} />
            )}
            {tab === "programs" && (
              <ProgramsPanel form={form} setForm={setForm} setLab={setLab} user={user} lab={lab} />
            )}
            {tab === "language" && (
              <LanguagePanel form={form} setForm={setForm} lab={lab} setLab={setLab} user={user} />
            )}
          </div>
        </div>

        {/* ============ RIGHT: Sticky preview (desktop, branding mode only, hidden in studio tab which has its own canvas) ============ */}
        {mode !== "settings" && tab !== "studio" && (
          <div className="hidden lg:block">
            <div className="sticky top-6" data-testid="sticky-preview">
              <LetterheadLivePreview form={form} compact />
              <div className="mt-3 text-[11px] text-ink/40 text-center">Preview auto-updates as you tweak settings on the left.</div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile preview drawer (branding mode only) */}
      {mode !== "settings" && previewOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={() => setPreviewOpen(false)}>
          <div className="absolute inset-x-0 bottom-0 max-h-[92vh] bg-white rounded-t-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="font-heading font-semibold text-ink text-sm">Live preview</div>
              <button type="button" onClick={() => setPreviewOpen(false)} className="w-8 h-8 grid place-items-center rounded hover:bg-muted" aria-label="Close preview">✕</button>
            </div>
            <div className="p-3 overflow-y-auto max-h-[85vh]">
              <LetterheadLivePreview form={form} compact />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SubNav({ tabs, value, onChange }) {
  return (
    <div className="rounded-2xl border border-border bg-white overflow-x-auto" data-testid="branding-subnav">
      <div className="flex gap-1 p-1.5 min-w-max">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => onChange(t.id)} data-testid={`subnav-${t.id}`}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${value === t.id ? "bg-ink text-white shadow" : "text-ink/70 hover:bg-muted"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ================= Identity panel (Lab info form + contact/registration details) ================= */
function IdentityPanel({ form, setForm, save, uploading, fileInputRef, onLogoFile, onSave }) {
  return (
    <div className="space-y-6">
      <Card className="p-6 rounded-2xl" data-testid="identity-card">
        <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><Palette className="w-5 h-5 text-brand" /> Lab identity</div>
        <p className="text-sm text-ink/60 mt-1">Core info that powers your smart reports and public listing.</p>
        <form onSubmit={save} className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Lab name</Label><Input data-testid="brand-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div><Label>URL slug</Label><Input data-testid="brand-slug" value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} required /></div>
            <div><Label>Contact email</Label><Input data-testid="brand-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>City</Label><Input data-testid="brand-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Bengaluru" /></div>
            <div><Label>WhatsApp number</Label><Input data-testid="brand-whatsapp" value={form.whatsapp_number || ""} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="+91 98..." /></div>
            <div><Label>UPI ID</Label><Input data-testid="brand-upi" value={form.upi_id || ""} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="yourlab@paytm" /></div>
            <div>
              <Label>Accreditations</Label>
              <Input data-testid="brand-accreditations" value={(form.accreditations || []).join(", ")} onChange={(e) => setForm({ ...form, accreditations: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="NABL, ICMR, ISO 9001" />
            </div>
            <div><Label>Rating (1-5)</Label><Input type="number" step="0.1" min="0" max="5" value={form.rating || ""} onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Turnaround (hours)</Label><Input type="number" value={form.turnaround_hours || 24} onChange={(e) => setForm({ ...form, turnaround_hours: parseInt(e.target.value) || 24 })} /></div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={form.home_collection !== false} onChange={(e) => setForm({ ...form, home_collection: e.target.checked })} data-testid="brand-home-coll" />
              <span className="text-sm text-ink/80">Offer free home sample collection</span>
            </div>
            <div className="sm:col-span-2"><Label>Tagline</Label><Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Trusted by 12,000+ patients across Bengaluru" /></div>
            <div className="sm:col-span-2"><Label>Website</Label><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></div>
            <div className="sm:col-span-2">
              <Label>Logo</Label>
              <div className="flex gap-2 items-start">
                <Input data-testid="brand-logo-url" value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https:// or upload below" />
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="brand-logo-file" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="brand-logo-upload">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload
                </Button>
              </div>
              <p className="text-xs text-ink/50 mt-1">PNG/JPG under 2 MB. Uploaded to secure object storage.</p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:col-span-2">
              <div><Label>Primary</Label><Input data-testid="brand-primary" type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-11 p-1" /></div>
              <div><Label>Secondary</Label><Input data-testid="brand-secondary" type="color" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="h-11 p-1" /></div>
            </div>
          </div>
          <Button data-testid="brand-save" type="submit" className="bg-ink hover:bg-ink/90 text-white h-11 px-6">Save identity</Button>
        </form>
      </Card>

      <ContactDetailsCard form={form} setForm={setForm} onSave={onSave} />
    </div>
  );
}

/* ================= Booking Settings — how Book-Now routes patients ================= */
function BookingSettingsPanel({ form, setForm, onSave, labId }) {
  const [saving, setSaving] = useState(false);
  const mode = form.booking_mode || "whatsapp";
  const setMode = (m) => setForm({ ...form, booking_mode: m });
  const publicUrl = form.slug ? `${window.location.origin}/l/${form.slug}/book` : "";

  const uploadBanner = async (file) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) { toast.error("Banner must be under 5 MB"); return null; }
    if (!file.type?.startsWith("image/")) { toast.error("Please upload an image file"); return null; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return `${API_BASE}/files/${r.data.path}`;
    } catch (err) {
      toast.error(apiError(err, "Banner upload failed"));
      return null;
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        booking_mode: form.booking_mode,
        booking_whatsapp_number: form.booking_whatsapp_number,
        booking_notify_email: form.booking_notify_email,
        booking_branches: form.booking_branches || [],
        booking_promo_banner_url: form.booking_promo_banner_url || null,
        booking_promo_banner_link: form.booking_promo_banner_link || null,
        promo_banners: form.promo_banners || [],
        promo_codes: form.promo_codes || [],
        booking_collection_hours: form.booking_collection_hours || [],
        booking_slot_interval_min: Number(form.booking_slot_interval_min || 30),
      });
    } finally {
      setSaving(false);
    }
  };

  const addBranch = () => setForm({
    ...form,
    booking_branches: [...(form.booking_branches || []), { id: Math.random().toString(36).slice(2, 8), name: "", city: "", address: "", phone: "" }],
  });
  const updateBranch = (i, patch) => {
    const list = [...(form.booking_branches || [])];
    list[i] = { ...list[i], ...patch };
    setForm({ ...form, booking_branches: list });
  };
  const removeBranch = (i) => {
    const list = [...(form.booking_branches || [])];
    list.splice(i, 1);
    setForm({ ...form, booking_branches: list });
  };

  return (
    <div className="space-y-6" data-testid="booking-settings-panel">
      <Card className="p-6 rounded-2xl">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-brand" />
          <h3 className="font-heading font-semibold text-ink text-lg">Booking method</h3>
        </div>
        <p className="text-sm text-ink/60 mt-1">Choose what happens when a patient clicks <b>Book Now</b> on their Smart Report.</p>

        <div className="mt-4 grid gap-3">
          <ModeCard
            active={mode === "whatsapp"}
            onClick={() => setMode("whatsapp")}
            icon={<MessageCircle className="w-4 h-4" />}
            title="WhatsApp (recommended)"
            desc="Opens WhatsApp with a pre-filled message: patient details, report ID, recommended tests, and a link to the smart report."
            testid="booking-mode-whatsapp"
          />
          <ModeCard
            active={mode === "efgh_page"}
            onClick={() => setMode("efgh_page")}
            icon={<ExternalLink className="w-4 h-4" />}
            title="EFGH Booking Page"
            desc={`Opens your public booking page: ${publicUrl || "(save your identity first to see your public URL)"}`}
            testid="booking-mode-efgh"
          />
          <ModeCard
            active={mode === "lab_api"}
            onClick={() => setMode("lab_api")}
            icon={<ShieldCheck className="w-4 h-4" />}
            title="Laboratory API (coming soon)"
            desc="Route Book-Now clicks to your own booking API. v1 falls back to the EFGH booking page."
            testid="booking-mode-api"
            disabled
          />
        </div>

        {mode === "whatsapp" && (
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Booking WhatsApp number *</Label>
              <Input
                data-testid="booking-wa-number"
                value={form.booking_whatsapp_number || ""}
                onChange={(e) => setForm({ ...form, booking_whatsapp_number: e.target.value })}
                placeholder="+919999900000"
              />
              <p className="text-[11px] text-ink/50 mt-1">Include country code. Empty = falls back to your public booking page.</p>
            </div>
            <div>
              <Label>Booking notification email</Label>
              <Input
                data-testid="booking-notify-email"
                value={form.booking_notify_email || ""}
                onChange={(e) => setForm({ ...form, booking_notify_email: e.target.value })}
                placeholder={form.email}
              />
              <p className="text-[11px] text-ink/50 mt-1">Booking requests also ping this email. Defaults to your lab's contact email.</p>
            </div>
          </div>
        )}

        {mode !== "whatsapp" && (
          <div className="mt-5">
            <Label>Booking notification email</Label>
            <Input
              data-testid="booking-notify-email"
              value={form.booking_notify_email || ""}
              onChange={(e) => setForm({ ...form, booking_notify_email: e.target.value })}
              placeholder={form.email}
            />
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-2xl">
        <div className="flex items-center gap-2">
          <FileImage className="w-5 h-5 text-brand" />
          <h3 className="font-heading font-semibold text-ink text-lg">Promotional banner (shared reports)</h3>
        </div>
        <p className="text-sm text-ink/60 mt-1">Wide image (recommended 1200 × 200) shown across the top of the public shared report. Upload a PNG/JPG (max 5 MB) or paste a URL.</p>

        {/* Upload / preview */}
        <div className="mt-4 grid sm:grid-cols-[220px_1fr] gap-4">
          <div>
            <Label className="text-xs">Banner image</Label>
            <div className="mt-1 rounded-lg border border-dashed border-border/70 p-3 bg-muted/30">
              {form.booking_promo_banner_url ? (
                <div className="space-y-2">
                  <img
                    src={form.booking_promo_banner_url}
                    alt="Banner preview"
                    className="w-full h-24 object-cover rounded"
                    data-testid="booking-promo-banner-preview"
                  />
                  <div className="flex gap-2">
                    <label className="flex-1 text-center text-[11px] cursor-pointer rounded-md border border-border bg-white px-2 py-1.5 hover:bg-muted transition-colors">
                      Replace
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="booking-promo-banner-file"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const url = await uploadBanner(f);
                          if (url) {
                            setForm((p) => ({ ...p, booking_promo_banner_url: url }));
                            toast.success("Banner uploaded — click Save to apply");
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      data-testid="booking-promo-banner-remove"
                      onClick={() => setForm((p) => ({ ...p, booking_promo_banner_url: null }))}
                      className="text-[11px] rounded-md border border-danger/50 text-danger bg-white px-2 py-1.5 hover:bg-danger/5"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label className="block cursor-pointer text-center py-6 hover:bg-white rounded transition-colors">
                  <FileImage className="w-6 h-6 mx-auto text-ink/40" />
                  <div className="mt-1.5 text-[12px] font-medium text-ink">Upload banner image</div>
                  <div className="text-[10px] text-ink/50">PNG or JPG · max 5 MB</div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="booking-promo-banner-file"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const url = await uploadBanner(f);
                      if (url) {
                        setForm((p) => ({ ...p, booking_promo_banner_url: url }));
                        toast.success("Banner uploaded — click Save to apply");
                      }
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs">Banner image URL (or paste)</Label>
              <Input
                data-testid="booking-promo-banner-url"
                value={form.booking_promo_banner_url || ""}
                onChange={(e) => setForm({ ...form, booking_promo_banner_url: e.target.value })}
                placeholder="https://…/banner.jpg"
              />
            </div>
            <div>
              <Label className="text-xs">Click-through link (optional)</Label>
              <Input
                data-testid="booking-promo-banner-link"
                value={form.booking_promo_banner_link || ""}
                onChange={(e) => setForm({ ...form, booking_promo_banner_link: e.target.value })}
                placeholder="https://your-site.com/offers"
              />
              <div className="text-[10.5px] text-ink/50 mt-1">When set, clicking the banner opens this URL in a new tab.</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Multiple promo banners — carousel on public pages */}
      <PromoBannersCard form={form} setForm={setForm} uploadBanner={uploadBanner} labId={labId} />

      {/* Lab-created promo/discount codes patients can apply */}
      <PromoCodesCard form={form} setForm={setForm} />

      <CollectionHoursCard form={form} setForm={setForm} />

      <Card className="p-6 rounded-2xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-heading font-semibold text-ink text-lg">Branches (optional)</h3>
            <p className="text-sm text-ink/60">Let patients pick a branch on the EFGH booking page when they choose "Lab Visit".</p>
          </div>
          <Button size="sm" onClick={addBranch} className="bg-ink hover:bg-ink/90 text-white" data-testid="booking-add-branch">
            <Plus className="w-4 h-4 mr-1" /> Add branch
          </Button>
        </div>
        <div className="mt-4 space-y-3">
          {(form.booking_branches || []).length === 0 && (
            <div className="text-sm text-ink/50 py-4 text-center rounded-lg border border-dashed border-border">No branches configured yet.</div>
          )}
          {(form.booking_branches || []).map((b, i) => (
            <div key={b.id || i} className="grid sm:grid-cols-[1fr_1fr_2fr_1fr_auto] gap-2 items-center p-3 rounded-xl border border-border">
              <Input placeholder="Branch name" value={b.name || ""} onChange={(e) => updateBranch(i, { name: e.target.value })} data-testid={`booking-branch-name-${i}`} />
              <Input placeholder="City" value={b.city || ""} onChange={(e) => updateBranch(i, { city: e.target.value })} data-testid={`booking-branch-city-${i}`} />
              <Input placeholder="Address" value={b.address || ""} onChange={(e) => updateBranch(i, { address: e.target.value })} data-testid={`booking-branch-addr-${i}`} />
              <Input placeholder="Phone" value={b.phone || ""} onChange={(e) => updateBranch(i, { phone: e.target.value })} data-testid={`booking-branch-phone-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => removeBranch(i)} data-testid={`booking-branch-remove-${i}`}>
                <Trash2 className="w-4 h-4 text-danger" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-ink hover:bg-ink/90 text-white h-11 px-6" data-testid="booking-save">
          {saving ? "Saving…" : "Save booking settings"}
        </Button>
      </div>
    </div>
  );
}

function ModeCard({ active, onClick, icon, title, desc, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testid}
      className={`text-left p-4 rounded-xl border transition-all ${active ? "border-brand bg-brand/5 shadow-sm" : "border-border hover:border-ink/30"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <div className="flex items-center gap-2 font-heading font-semibold text-ink text-sm">{icon}{title}</div>
      <div className="text-xs text-ink/60 mt-1 leading-relaxed">{desc}</div>
    </button>
  );
}

/* ================= Collection hours editor — controls the 30-min slot picker on the EFGH booking page ================= */
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* ────────────────────────────────────────────────────────────────────────
 * PromoBannersCard — CRUD for multiple promotional banners that render as
 * a swipeable auto-rotating carousel on the public booking page AND on the
 * public smart-report share link. Order matters (drag-not-yet, use ↑↓).
 * ──────────────────────────────────────────────────────────────────────── */
function PromoBannersCard({ form, setForm, uploadBanner, labId }) {
  const banners = form.promo_banners || [];
  const setBanners = (next) => setForm({ ...form, promo_banners: next });
  const [stats, setStats] = React.useState({});  // {banner_id: {impressions, clicks, ctr}}
  React.useEffect(() => {
    if (!labId) return;
    let alive = true;
    api.get(`/public/labs/${labId}/banners/analytics`).then((r) => {
      if (!alive) return;
      const map = {};
      (r.data?.banners || []).forEach((b) => { map[b.id] = b; });
      setStats(map);
    }).catch(() => {});
    return () => { alive = false; };
  }, [labId, banners.length]);
  const update = (i, patch) => setBanners(banners.map((b, k) => (k === i ? { ...b, ...patch } : b)));
  const remove = (i) => setBanners(banners.filter((_, k) => k !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= banners.length) return;
    const arr = [...banners];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setBanners(arr);
  };
  const add = () => setBanners([
    ...banners,
    { id: `b-${Date.now()}`, image_url: "", link_url: "", title: "", active: true },
  ]);

  return (
    <Card className="p-6 rounded-2xl" data-testid="promo-banners-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <FileImage className="w-5 h-5 text-efgh-orange" />
            <h3 className="font-heading font-semibold text-ink text-lg">Promotional banners (carousel)</h3>
          </div>
          <p className="text-sm text-ink/60 mt-1">Add up to 5 banners. Active banners rotate every 5s on the public booking page and shared smart-report header. Order top→bottom = first→last in carousel.</p>
        </div>
        <Button size="sm" onClick={add} data-testid="add-promo-banner" disabled={banners.length >= 5}><Plus className="w-4 h-4 mr-1" /> Add banner</Button>
      </div>

      {banners.length === 0 && (
        <div className="mt-4 p-6 border border-dashed border-border rounded-xl text-center text-sm text-ink/50">
          No promotional banners yet. Click &quot;Add banner&quot; to create one.
        </div>
      )}

      <div className="mt-4 space-y-3">
        {banners.map((b, i) => (
          <div key={b.id || i} className={`grid sm:grid-cols-[110px_1fr_auto] gap-3 p-3 border rounded-xl ${b.active ? "border-border" : "border-dashed border-ink/20 opacity-60"}`} data-testid={`promo-banner-${i}`}>
            <div className="rounded-lg overflow-hidden bg-muted h-16 grid place-items-center">
              {b.image_url
                ? <img src={b.image_url} alt="" className="w-full h-full object-cover" />
                : <FileImage className="w-5 h-5 text-ink/30" />}
            </div>
            <div className="space-y-1.5 min-w-0">
              <Input placeholder="Title (optional, e.g. Free home collection this weekend)" value={b.title || ""} onChange={(e) => update(i, { title: e.target.value })} data-testid={`promo-banner-${i}-title`} />
              <div className="grid sm:grid-cols-2 gap-1.5">
                <div className="flex gap-1.5">
                  <Input className="flex-1 text-xs" placeholder="Image URL" value={b.image_url || ""} onChange={(e) => update(i, { image_url: e.target.value })} data-testid={`promo-banner-${i}-url`} />
                  <label className="cursor-pointer inline-flex items-center px-2 py-1.5 border border-border rounded-md bg-white hover:bg-muted text-xs">
                    Upload
                    <input type="file" accept="image/*" className="hidden" data-testid={`promo-banner-${i}-file`} onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const url = await uploadBanner(f);
                      if (url) { update(i, { image_url: url }); toast.success("Uploaded — click Save"); }
                      e.target.value = "";
                    }} />
                  </label>
                </div>
                <Input className="text-xs" placeholder="Click-through URL (optional)" value={b.link_url || ""} onChange={(e) => update(i, { link_url: e.target.value })} data-testid={`promo-banner-${i}-link`} />
              </div>
              {/* Impression / click analytics — read-only, updates on save */}
              {b.id && stats[b.id] && (
                <div className="flex flex-wrap items-center gap-3 pt-1" data-testid={`promo-banner-${i}-stats`}>
                  <span className="text-[10.5px] uppercase tracking-widest text-ink/40 font-semibold">Analytics</span>
                  <span className="text-[11px] text-ink/70"><b>{stats[b.id].impressions.toLocaleString()}</b> impressions</span>
                  <span className="text-[11px] text-ink/70"><b>{stats[b.id].clicks.toLocaleString()}</b> clicks</span>
                  <span className="text-[11px] text-ink/70">CTR <b>{stats[b.id].ctr}%</b></span>
                </div>
              )}
            </div>
            <div className="flex sm:flex-col items-center sm:items-end gap-1.5 justify-end">
              <label className="flex items-center gap-1 text-[11px] text-ink/70">
                <input type="checkbox" checked={!!b.active} onChange={(e) => update(i, { active: e.target.checked })} data-testid={`promo-banner-${i}-active`} /> Active
              </label>
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-muted disabled:opacity-30" data-testid={`promo-banner-${i}-up`}>↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === banners.length - 1} className="p-1 rounded hover:bg-muted disabled:opacity-30" data-testid={`promo-banner-${i}-down`}>↓</button>
                <button type="button" onClick={() => remove(i)} className="p-1 rounded text-danger hover:bg-danger/10" data-testid={`promo-banner-${i}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 * PromoCodesCard — lab-owned discount codes that appear on the public
 * booking page as one-tap chips. Purely metadata for now — the lab applies
 * the discount manually at confirmation time (or a future auto-apply job).
 * ──────────────────────────────────────────────────────────────────────── */
function PromoCodesCard({ form, setForm }) {
  const codes = form.promo_codes || [];
  const setCodes = (next) => setForm({ ...form, promo_codes: next });
  const update = (i, patch) => setCodes(codes.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const remove = (i) => setCodes(codes.filter((_, k) => k !== i));
  const add = () => setCodes([
    ...codes,
    { id: `p-${Date.now()}`, code: "", label: "", discount_type: "percent", value: 10, min_amount: 0, max_discount: 0, applies_to: "all", description: "", active: true },
  ]);

  return (
    <Card className="p-6 rounded-2xl" data-testid="promo-codes-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-efgh-green" />
            <h3 className="font-heading font-semibold text-ink text-lg">Promo codes & offers</h3>
          </div>
          <p className="text-sm text-ink/60 mt-1">Codes shown as tap-to-apply chips on the public booking page. Patients see only <b>active</b> codes within their valid window.</p>
        </div>
        <Button size="sm" onClick={add} data-testid="add-promo-code"><Plus className="w-4 h-4 mr-1" /> Add code</Button>
      </div>

      {codes.length === 0 && (
        <div className="mt-4 p-6 border border-dashed border-border rounded-xl text-center text-sm text-ink/50">
          No promo codes yet. Create WELCOME10, FEBFIT, etc.
        </div>
      )}

      <div className="mt-4 space-y-3">
        {codes.map((c, i) => (
          <div key={c.id || i} className={`p-3 border rounded-xl ${c.active ? "border-border" : "border-dashed border-ink/20 opacity-60"}`} data-testid={`promo-code-${i}`}>
            <div className="grid sm:grid-cols-[130px_1fr_120px_auto] gap-2 items-end">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-ink/50">Code</Label>
                <Input className="font-mono uppercase" value={c.code} onChange={(e) => update(i, { code: e.target.value.toUpperCase() })} placeholder="WELCOME10" data-testid={`promo-code-${i}-code`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-ink/50">Label / Description</Label>
                <Input value={c.description || ""} onChange={(e) => update(i, { description: e.target.value })} placeholder="10% off first booking" data-testid={`promo-code-${i}-desc`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-ink/50">Discount</Label>
                <div className="flex gap-1">
                  <Input type="number" min="0" value={c.value} onChange={(e) => update(i, { value: Number(e.target.value) })} className="w-16" data-testid={`promo-code-${i}-value`} />
                  <select value={c.discount_type} onChange={(e) => update(i, { discount_type: e.target.value })} className="border border-border rounded-md px-1 text-xs" data-testid={`promo-code-${i}-type`}>
                    <option value="percent">%</option>
                    <option value="flat">₹</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <label className="flex items-center gap-1 text-[11px] text-ink/70">
                  <input type="checkbox" checked={!!c.active} onChange={(e) => update(i, { active: e.target.checked })} data-testid={`promo-code-${i}-active`} /> Active
                </label>
                <button type="button" onClick={() => remove(i)} className="p-1 rounded text-danger hover:bg-danger/10" data-testid={`promo-code-${i}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="mt-2 grid sm:grid-cols-4 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-ink/50">Applies to</Label>
                <select value={c.applies_to || "all"} onChange={(e) => update(i, { applies_to: e.target.value })} className="w-full border border-border rounded-md px-2 py-1.5 text-xs" data-testid={`promo-code-${i}-applies`}>
                  <option value="all">All bookings</option>
                  <option value="tests">Tests only</option>
                  <option value="packages">Packages only</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-ink/50">Min amount (₹)</Label>
                <Input type="number" min="0" value={c.min_amount || 0} onChange={(e) => update(i, { min_amount: Number(e.target.value) })} data-testid={`promo-code-${i}-min`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-ink/50">Valid from</Label>
                <Input type="date" value={c.valid_from || ""} onChange={(e) => update(i, { valid_from: e.target.value })} data-testid={`promo-code-${i}-from`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-ink/50">Valid till</Label>
                <Input type="date" value={c.valid_till || ""} onChange={(e) => update(i, { valid_till: e.target.value })} data-testid={`promo-code-${i}-till`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}


function CollectionHoursCard({ form, setForm }) {
  const stored = form.booking_collection_hours || [];
  // If the lab has never customised hours, seed the visual state with EFGH default (Mon-Sat 07:00-19:00)
  // so the card reflects the actual behaviour instead of appearing all-unchecked. The seed is only
  // written to `form` on the first user interaction (below).
  const isDefault = stored.length === 0;
  const DEFAULT = React.useMemo(
    () => Array.from({ length: 6 }, (_, d) => ({ day: d, start: "07:00", end: "19:00" })),
    []
  );
  const hours = isDefault ? DEFAULT : stored;
  const byDay = WEEKDAYS.map((label, day) => ({
    label,
    day,
    row: hours.find((h) => Number(h.day) === day) || null,
  }));

  const setDay = (day, patch) => {
    // First mutation "materialises" the default so subsequent state persists correctly.
    const base = isDefault ? DEFAULT : stored;
    const next = base.filter((h) => Number(h.day) !== day);
    if (patch) next.push({ day, start: patch.start ?? "07:00", end: patch.end ?? "19:00" });
    next.sort((a, b) => Number(a.day) - Number(b.day));
    setForm({ ...form, booking_collection_hours: next });
  };

  return (
    <Card className="p-6 rounded-2xl" data-testid="collection-hours-card">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-5 h-5 text-brand" />
        <h3 className="font-heading font-semibold text-ink text-lg">Collection hours</h3>
      </div>
      <p className="text-sm text-ink/60 mt-1">
        Slots on the EFGH booking page are 30-min intervals within these windows. Leave a day unchecked to mark it closed.
        Empty = EFGH default (Mon-Sat 07:00-19:00).
      </p>
      <div className="mt-4 grid gap-2">
        {byDay.map(({ label, day, row }) => {
          const open = !!row;
          return (
            <div key={day} className="grid grid-cols-[64px_36px_1fr_16px_1fr] items-center gap-3 py-1" data-testid={`ch-row-${day}`}>
              <div className="text-sm font-medium text-ink w-16">{label}</div>
              <input
                type="checkbox"
                checked={open}
                onChange={(e) => setDay(day, e.target.checked ? { start: row?.start || "07:00", end: row?.end || "19:00" } : null)}
                data-testid={`ch-open-${day}`}
                className="w-4 h-4 accent-brand"
              />
              <Input
                type="time"
                value={row?.start || ""}
                disabled={!open}
                onChange={(e) => setDay(day, { start: e.target.value, end: row?.end || "19:00" })}
                data-testid={`ch-start-${day}`}
              />
              <div className="text-ink/40 text-center">→</div>
              <Input
                type="time"
                value={row?.end || ""}
                disabled={!open}
                onChange={(e) => setDay(day, { start: row?.start || "07:00", end: e.target.value })}
                data-testid={`ch-end-${day}`}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-5">
        <Label className="text-xs">Slot interval (minutes)</Label>
        <select
          data-testid="ch-slot-interval"
          value={Number(form.booking_slot_interval_min || 30)}
          onChange={(e) => setForm({ ...form, booking_slot_interval_min: Number(e.target.value) })}
          className="w-full mt-1 rounded-lg border border-border px-3 py-2 text-sm h-10 max-w-[220px]"
        >
          {[15, 20, 30, 45, 60].map((n) => <option key={n} value={n}>{`${n} minutes`}</option>)}
        </select>
      </div>
    </Card>
  );
}

/* ================= Assets panel — accreditations + custom logos + doctor signatures ================= */
function CoverPageCard({ form, setForm, lab }) {
  const { platform, refresh: refreshSettings } = useSiteSettings();
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileRef = useRef(null);
  const enabled = (platform?.cover_page_enabled_labs || []).includes(lab?.id);
  const currentUrl = form?.cover_page_url;
  const currentName = form?.cover_page_filename;

  useEffect(() => { if (refreshSettings) refreshSettings(); }, []);

  if (!enabled) {
    return (
      <Card className="p-6 rounded-2xl border-dashed" data-testid="cover-page-card-disabled">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center"><FileImage className="w-5 h-5 text-ink/40" /></div>
          <div>
            <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2">
              PDF cover page
              <span className="pill bg-muted text-ink/50 text-[10px]">Locked</span>
            </div>
            <p className="text-sm text-ink/60 mt-1">
              Upload a pre-designed PDF cover (≤ 1 MB) that will be attached as page 1 of every downloaded Smart Report for your lab.
              This feature is opt-in — contact EFGH support to have it enabled for your lab.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const onFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Only PDF files are supported"); return; }
    if (file.size > 1 * 1024 * 1024) { toast.error("PDF must be ≤ 1 MB"); return; }
    if (!lab?.id) { toast.error("Lab not loaded yet — try again in a moment"); return; }

    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(`/labs/${lab.id}/cover-page`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({
        ...prev,
        cover_page_url: r.data.url,
        cover_page_path: r.data.path,
        cover_page_filename: r.data.filename,
      }));
      toast.success("Cover page uploaded");
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove the PDF cover page? Future report downloads will no longer include it.")) return;
    setRemoving(true);
    try {
      await api.delete(`/labs/${lab.id}/cover-page`);
      setForm((prev) => ({ ...prev, cover_page_url: null, cover_page_path: null, cover_page_filename: null }));
      toast.success("Cover page removed");
    } catch (e) { toast.error(apiError(e, "Remove failed")); }
    finally { setRemoving(false); }
  };

  return (
    <Card className="p-6 rounded-2xl" data-testid="cover-page-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2">
            <FileImage className="w-5 h-5 text-efgh-blue" /> PDF cover page
            <span className="pill pill-normal text-[10px]">Enabled by EFGH</span>
          </div>
          <p className="text-sm text-ink/60 mt-1">
            Upload a pre-designed PDF (max 1 MB). It will be attached as the first page of every downloaded Smart Report for your lab.
          </p>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-4 items-start">
        <label className="border-2 border-dashed border-border rounded-xl p-6 text-center bg-muted/30 hover:bg-muted/60 cursor-pointer block">
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="cover-page-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
          <Upload className="w-6 h-6 mx-auto text-ink/50" />
          <div className="text-sm font-medium text-ink mt-2">
            {uploading ? "Uploading…" : currentUrl ? "Replace cover PDF" : "Upload cover PDF"}
          </div>
          <div className="text-xs text-ink/50 mt-1">PDF only · max 1 MB</div>
        </label>

        {currentUrl && (
          <div className="border border-border rounded-xl p-4 min-w-[220px] flex flex-col items-start gap-2" data-testid="cover-page-current">
            <div className="text-xs text-ink/50">Current cover</div>
            <div className="text-sm font-medium text-ink truncate max-w-[200px]" title={currentName || "cover.pdf"}>{currentName || "cover.pdf"}</div>
            <div className="flex items-center gap-2 pt-1">
              <a href={currentUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-efgh-blue hover:underline" data-testid="cover-page-preview">
                <ExternalLink className="w-3 h-3" /> Preview
              </a>
              <button type="button" onClick={onRemove} disabled={removing} className="inline-flex items-center gap-1 text-xs text-danger hover:underline" data-testid="cover-page-remove">
                <Trash2 className="w-3 h-3" /> {removing ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function AssetSizeSlider({ label, value, defaultValue, min = 12, max = 120, onChange, testid }) {
  const v = Number.isFinite(value) ? value : defaultValue;
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider text-ink/50 w-8 shrink-0">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        value={v}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="flex-1 h-1 accent-efgh-blue cursor-pointer"
      />
      <span className="tabular-nums text-[10px] text-ink/60 w-9 shrink-0 text-right">{v}px</span>
    </div>
  );
}


function AssetsPanel({ form, setForm, lab, addAccreditation, removeAccreditation, addCustomLogo, removeCustomLogo, addSignature, removeSignature, setDefaultSignature, patchAssetItem }) {
  return (
    <div className="space-y-6">
      <CoverPageCard form={form} setForm={setForm} lab={lab} />

      <Card className="p-6 rounded-2xl" data-testid="accreditation-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><Award className="w-5 h-5 text-efgh-orange" /> Accreditation logos</div>
            <p className="text-sm text-ink/60">NABL, CAP, ISO 9001, ICMR etc. — appear on the smart-report letterhead.</p>
          </div>
          <label className="inline-flex">
            <input type="file" accept="image/*" className="hidden" data-testid="add-accreditation-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) addAccreditation(f); e.target.value = ""; }} />
            <span className="inline-flex items-center h-10 px-4 rounded-md bg-ink text-white text-sm font-medium cursor-pointer hover:bg-ink/90"><Plus className="w-4 h-4 mr-2" /> Add logo</span>
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {(form.accreditation_logos || []).length === 0 && (
            <div className="text-sm text-ink/40 py-6 text-center w-full border border-dashed rounded-xl">No accreditation logos yet.</div>
          )}
          {(form.accreditation_logos || []).map((a, i) => (
            <div key={i} className="p-3 rounded-xl border border-border flex flex-col gap-2 min-w-[240px]">
              <div className="flex items-center gap-3">
                <img src={a.logo_url} alt={a.label} className="w-12 h-12 rounded object-contain bg-muted p-1" />
                <div className="flex-1"><div className="text-sm font-medium text-ink">{a.label}</div></div>
                <button type="button" onClick={() => removeAccreditation(i)} data-testid={`remove-accred-${i}`} className="text-ink/40 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
              </div>
              <AssetSizeSlider label="Size" value={a.height_px} defaultValue={36} min={16} max={100} onChange={(v) => patchAssetItem && patchAssetItem("accreditation", i, { height_px: v })} testid={`accred-size-${i}`} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 rounded-2xl" data-testid="custom-logos-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><Award className="w-5 h-5 text-efgh-green" /> Custom / other logos</div>
            <p className="text-sm text-ink/60">Franchise, partner, chain-affiliation or award logos. Drop them into any header/footer slot.</p>
          </div>
          <label className="inline-flex">
            <input type="file" accept="image/*" className="hidden" data-testid="add-custom-logo-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) addCustomLogo(f); e.target.value = ""; }} />
            <span className="inline-flex items-center h-10 px-4 rounded-md bg-ink text-white text-sm font-medium cursor-pointer hover:bg-ink/90"><Plus className="w-4 h-4 mr-2" /> Add logo</span>
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {(form.custom_logos || []).length === 0 && (
            <div className="text-sm text-ink/40 py-6 text-center w-full border border-dashed rounded-xl">No custom logos yet.</div>
          )}
          {(form.custom_logos || []).map((c, i) => (
            <div key={c.id || i} className="p-3 rounded-xl border border-border flex flex-col gap-2 min-w-[240px]" data-testid={`custom-logo-${i}`}>
              <div className="flex items-center gap-3">
                <img src={c.logo_url} alt={c.label} className="w-12 h-12 rounded object-contain bg-muted p-1" />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium text-ink truncate">{c.label}</div></div>
                <button type="button" onClick={() => removeCustomLogo(i)} data-testid={`remove-custom-logo-${i}`} className="text-ink/40 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
              </div>
              <AssetSizeSlider label="Size" value={c.height_px} defaultValue={36} min={16} max={120} onChange={(v) => patchAssetItem && patchAssetItem("custom", i, { height_px: v })} testid={`custom-size-${i}`} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 rounded-2xl" data-testid="signatures-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><PenLine className="w-5 h-5 text-efgh-orange" /> Doctor signatures</div>
            <p className="text-sm text-ink/60">Transparent PNGs. Authorised reviewers pick one at publish time.</p>
          </div>
          <label className="inline-flex">
            <input type="file" accept="image/png,image/*" className="hidden" data-testid="add-signature-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) addSignature(f); e.target.value = ""; }} />
            <span className="inline-flex items-center h-10 px-4 rounded-md bg-ink text-white text-sm font-medium cursor-pointer hover:bg-ink/90"><Plus className="w-4 h-4 mr-2" /> Add signature</span>
          </label>
        </div>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {(form.doctor_signatures || []).length === 0 && (
            <div className="text-sm text-ink/40 py-6 text-center col-span-full border border-dashed rounded-xl">No signatures uploaded.</div>
          )}
          {(form.doctor_signatures || []).map((s, i) => (
            <div key={s.id || i} className="p-4 rounded-xl border border-border" data-testid={`sig-card-${i}`}>
              <div className="flex items-start gap-3">
                <img src={s.signature_url} alt={s.name} className="h-14 w-24 object-contain bg-muted rounded" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-ink truncate">Dr. {s.name}</div>
                  <div className="text-xs text-ink/60 truncate">{s.qualification}</div>
                  {s.registration_no && <div className="text-[11px] text-ink/50 mt-0.5">Reg: {s.registration_no}</div>}
                </div>
                <div className="flex flex-col gap-1">
                  <button type="button" onClick={() => setDefaultSignature(i)} data-testid={`sig-default-${i}`} title="Set as default" className={`p-1 rounded hover:bg-muted ${s.is_default ? "text-efgh-yellow" : "text-ink/30"}`}><Star className="w-4 h-4" fill={s.is_default ? "currentColor" : "none"} /></button>
                  <button type="button" onClick={() => removeSignature(i)} data-testid={`sig-remove-${i}`} className="p-1 rounded hover:bg-muted text-ink/40 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-3">
                <AssetSizeSlider label="Size" value={s.height_px} defaultValue={56} min={24} max={140} onChange={(v) => patchAssetItem && patchAssetItem("signature", i, { height_px: v })} testid={`sig-size-${i}`} />
              </div>
              {s.is_default && <div className="mt-2 text-[11px] text-efgh-yellow font-medium">Default signature</div>}
            </div>
          ))}
        </div>
      </Card>

      <p className="text-xs text-ink/50">All changes here are saved automatically as you add, remove or set a default.</p>
    </div>
  );
}

/* ================= Programs panel — reviewers + referral program + marketplace ================= */
const VISIBILITY_MODES = [
  {
    id: "public",
    label: "Public",
    tagline: "Discoverable · Listed on Marketplace",
    icon: Globe,
    color: "text-efgh-green",
    bg: "bg-efgh-green/10",
    bullets: [
      "Searchable in EFGH Marketplace",
      "Accepts public booking submissions",
      "Appears in city / test search results",
    ],
  },
  {
    id: "hidden",
    label: "Hidden",
    tagline: "Invitation-only · Direct URLs still work",
    icon: EyeOff,
    color: "text-efgh-orange",
    bg: "bg-efgh-orange/10",
    bullets: [
      "NOT searchable, NOT listed on Marketplace",
      "Existing Smart Report share links continue to work",
      "Existing booking URLs continue to work",
      "Best for private / referral-only labs",
    ],
  },
  {
    id: "private",
    label: "Private",
    tagline: "Enterprise · Auth required",
    icon: Lock,
    color: "text-danger",
    bg: "bg-danger/10",
    bullets: [
      "Completely hidden — no marketplace, no search",
      "No anonymous access to reports or booking pages",
      "Smart Reports accessible only via login / invitation",
      "Ideal for hospitals & corporate labs",
    ],
  },
];

function LanguagePanel({ form, setForm, lab, setLab, user }) {
  const { platform } = useSiteSettings();
  const [saving, setSaving] = useState(false);
  const labId = lab?.id || user?.lab_id;
  const globalOn = platform?.listen_enabled !== false;
  const perLabOff = (platform?.listen_disabled_labs || []).includes(labId);
  const listenActive = globalOn && !perLabOff;
  const currentLang = form.regional_language || platform?.default_regional_language || "hi";

  const LANGS = [
    { id: "hi", label: "हिन्दी",  en: "Hindi" },
    { id: "pa", label: "ਪੰਜਾਬੀ",   en: "Punjabi" },
    { id: "gu", label: "ગુજરાતી",  en: "Gujarati" },
    { id: "bn", label: "বাংলা",    en: "Bangla" },
    { id: "mr", label: "मराठी",    en: "Marathi" },
  ];

  const save = async (id) => {
    setSaving(true);
    const next = { ...form, regional_language: id };
    setForm(next);
    try {
      const r = await api.put(`/labs/${labId}`, next);
      setLab(r.data);
      toast.success("Regional language saved");
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    setSaving(false);
  };

  return (
    <Card className="p-6 rounded-2xl" data-testid="lab-language-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="font-heading font-semibold text-ink flex items-center gap-2">
            <Globe className="w-4 h-4 text-efgh-blue" /> Smart Report audio &amp; translated summary
          </div>
          <p className="text-sm text-ink/60 mt-1">Pick the second language patients can choose beside English on every report from this lab.</p>
        </div>
        <span className={`pill ${listenActive ? "pill-normal" : "pill-not_tested"} text-[10.5px]`} data-testid="lab-listen-status">
          {listenActive ? "Listen enabled" : globalOn ? "Disabled by super admin" : "Disabled globally"}
        </span>
      </div>
      <div className="mt-5">
        <Label className="text-xs uppercase tracking-wider text-ink/60">Regional language</Label>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="lab-regional-lang-grid">
          {LANGS.map((L) => {
            const active = currentLang === L.id;
            return (
              <button
                key={L.id}
                type="button"
                disabled={saving}
                onClick={() => save(L.id)}
                data-testid={`lab-lang-${L.id}`}
                className={`p-3 rounded-xl border-2 transition-all ${active ? "border-ink bg-ink/5" : "border-border hover:bg-muted"}`}
                title={L.en}
              >
                <div className="font-heading font-semibold text-sm text-ink truncate">{L.label}</div>
                <div className="text-[10.5px] text-ink/50 truncate">{L.en}</div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-ink/50 mt-3">English is always the default. Voice provider &amp; global on/off are managed by EFGH super admin.</p>
      </div>
    </Card>
  );
}

function ProgramsPanel({ form, setForm, setLab, user, lab }) {
  const currentMode = (() => {
    const m = (form.visibility_mode || "").toLowerCase();
    if (["public", "hidden", "private"].includes(m)) return m;
    return form.marketplace_listed !== false ? "public" : "hidden";
  })();

  const saveMode = async (mode) => {
    const next = {
      ...form,
      visibility_mode: mode,
      // Keep the legacy boolean in sync for backwards compat.
      marketplace_listed: mode === "public",
    };
    setForm(next);
    try {
      const r = await api.put(`/labs/${lab?.id || user.lab_id}`, next);
      setLab(r.data);
      toast.success(`Visibility set to ${mode.toUpperCase()}`);
    } catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 rounded-2xl" data-testid="visibility-card">
        <div>
          <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><Star className="w-5 h-5 text-efgh-yellow" /> Marketplace visibility</div>
          <p className="text-sm text-ink/60 mt-1 max-w-2xl">Choose how your lab appears on EFGH. You can change this anytime — nothing is destructive.</p>
        </div>
        <div className="mt-5 grid md:grid-cols-3 gap-3" data-testid="visibility-mode-group">
          {VISIBILITY_MODES.map((m) => {
            const Icon = m.icon;
            const active = currentMode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                data-testid={`visibility-mode-${m.id}`}
                aria-pressed={active}
                onClick={() => saveMode(m.id)}
                className={`text-left rounded-2xl border p-4 transition-all ${active ? "border-ink shadow-md bg-white" : "border-border hover:border-ink/40 bg-muted/40"}`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-lg ${m.bg} ${m.color} grid place-items-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className={`font-heading font-semibold text-ink ${active ? "" : ""}`}>{m.label}</div>
                    <div className="text-[11px] text-ink/60 leading-tight">{m.tagline}</div>
                  </div>
                  {active && <span className="ml-auto text-[10px] uppercase tracking-widest text-ink font-bold px-2 py-0.5 bg-efgh-yellow rounded-full">Active</span>}
                </div>
                <ul className="mt-3 space-y-1.5 text-xs text-ink/70">
                  {m.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-1.5"><span className={`w-1 h-1 mt-1.5 rounded-full ${m.color.replace("text-", "bg-")}`} />{b}</li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      </Card>

      <ReviewersCard />
      <ReferralProgramCard />
    </div>
  );
}

function ContactDetailsCard({ form, setForm, onSave }) {
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        customer_care: form.customer_care || "",
        toll_free: form.toll_free || "",
        emergency_contact: form.emergency_contact || "",
        working_hours: form.working_hours || "",
        license_no: form.license_no || "",
        gst_number: form.gst_number || "",
        cin_number: form.cin_number || "",
        registration_no: form.registration_no || "",
        social_handles: form.social_handles || [],
      });
    } finally { setSaving(false); }
  };
  const socials = form.social_handles || [];
  const addSocial = () => setForm({ ...form, social_handles: [...socials, { platform: "instagram", handle: "", url: "" }] });
  const removeSocial = (i) => setForm({ ...form, social_handles: socials.filter((_, idx) => idx !== i) });
  const updateSocial = (i, patch) => setForm({ ...form, social_handles: socials.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
  return (
    <Card className="mt-6 p-6 rounded-2xl" data-testid="contact-details-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><PenLine className="w-5 h-5 text-efgh-green" /> Contact &amp; registration details</div>
          <p className="text-sm text-ink/60">Drop any of these fields into your letterhead header or footer using the letterhead designer below.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-contact-details" className="bg-ink hover:bg-ink/90 text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save details
        </Button>
      </div>
      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div><Label>Customer care number</Label><Input data-testid="ct-customer-care" value={form.customer_care || ""} onChange={(e) => set("customer_care", e.target.value)} placeholder="+91 1800-xxx-xxxx" /></div>
        <div><Label>Toll-free number</Label><Input data-testid="ct-toll-free" value={form.toll_free || ""} onChange={(e) => set("toll_free", e.target.value)} placeholder="1800-123-4567" /></div>
        <div><Label>Emergency contact</Label><Input data-testid="ct-emergency" value={form.emergency_contact || ""} onChange={(e) => set("emergency_contact", e.target.value)} placeholder="+91 98..." /></div>
        <div><Label>Working hours</Label><Input data-testid="ct-hours" value={form.working_hours || ""} onChange={(e) => set("working_hours", e.target.value)} placeholder="Mon–Sat: 7am–9pm · Sun: 8am–2pm" /></div>
        <div><Label>Lab license no.</Label><Input data-testid="ct-license" value={form.license_no || ""} onChange={(e) => set("license_no", e.target.value)} placeholder="DL-2023-XXXXX" /></div>
        <div><Label>Registration no.</Label><Input data-testid="ct-registration" value={form.registration_no || ""} onChange={(e) => set("registration_no", e.target.value)} placeholder="e.g. NABL M-0001" /></div>
        <div><Label>GST number</Label><Input data-testid="ct-gst" value={form.gst_number || ""} onChange={(e) => set("gst_number", e.target.value)} placeholder="29ABCDE1234F1Z5" /></div>
        <div><Label>CIN number</Label><Input data-testid="ct-cin" value={form.cin_number || ""} onChange={(e) => set("cin_number", e.target.value)} placeholder="U85110KA2020PTC000000" /></div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold">Social handles</div>
          <Button type="button" variant="outline" onClick={addSocial} data-testid="add-social" className="h-8 text-xs"><Plus className="w-3 h-3 mr-1" /> Add social</Button>
        </div>
        {socials.length === 0 && <div className="text-sm text-ink/40 py-4 text-center w-full border border-dashed rounded-xl">No social handles yet.</div>}
        {socials.map((s, i) => (
          <div key={i} className="mt-2 grid grid-cols-[130px_1fr_1fr_36px] gap-2 items-center" data-testid={`social-row-${i}`}>
            <select value={s.platform} onChange={(e) => updateSocial(i, { platform: e.target.value })} className="h-9 rounded-md border border-border bg-white px-2 text-sm">
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="x">X (Twitter)</option>
              <option value="linkedin">LinkedIn</option>
              <option value="youtube">YouTube</option>
              <option value="threads">Threads</option>
              <option value="other">Other</option>
            </select>
            <Input value={s.handle || ""} onChange={(e) => updateSocial(i, { handle: e.target.value })} placeholder="@yourlab" />
            <Input value={s.url || ""} onChange={(e) => updateSocial(i, { url: e.target.value })} placeholder="https://..." />
            <button type="button" onClick={() => removeSocial(i)} className="text-ink/40 hover:text-danger" data-testid={`remove-social-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function FieldStylesCard({ form, setForm, onSave }) {
  const [saving, setSaving] = useState(false);
  const DEFAULTS = { logo_size: "md", font_family: "system", base_text_size: 11, text_color: "#374151", text_weight: 400 };
  const lh = { ...DEFAULTS, ...(form?.letterhead || {}) };
  const set = (k, v) => setForm({ ...form, letterhead: { ...form.letterhead, [k]: v } });
  const save = async () => {
    setSaving(true);
    try { await onSave(form.letterhead || lh); }
    finally { setSaving(false); }
  };
  return (
    <Card className="mt-6 p-6 rounded-2xl" data-testid="field-styles-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><Palette className="w-5 h-5 text-efgh-blue" /> Per-field style overrides</div>
          <p className="text-sm text-ink/60">Fine-tune font, size, weight &amp; colour for each individual letterhead field. Blank inputs inherit the global typography from the design card above.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-field-styles" className="bg-ink hover:bg-ink/90 text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save field styles
        </Button>
      </div>
      <FieldStylesEditor lh={lh} set={set} />
    </Card>
  );
}

function FieldStylesEditor({ lh, set }) {
  const FIELD_GROUPS = [
    { title: "Identity", keys: { name: "Lab name", tagline: "Tagline", address: "Address" } },
    { title: "Contact", keys: { contact: "Contact block", phone: "Phone", email: "Email", website: "Website", whatsapp: "WhatsApp", customer_care: "Customer care", toll_free: "Toll-free", emergency: "Emergency", working_hours: "Working hours" } },
    { title: "Registration", keys: { license: "License no.", gst: "GST no.", cin: "CIN no.", registration: "Registration no." } },
    { title: "Meta", keys: { social: "Social handles", timestamp: "Timestamp", report_id: "Report ID", custom: "Custom text", disclaimer: "Disclaimer" } },
  ];
  const styles = lh.field_styles || {};
  const setField = (key, patch) => {
    const next = { ...styles, [key]: { ...(styles[key] || {}), ...patch } };
    if (!next[key].size && !next[key].weight && !next[key].color && !next[key].family) delete next[key];
    set("field_styles", next);
  };
  const clearField = (key) => {
    const next = { ...styles };
    delete next[key];
    set("field_styles", next);
  };
  const FONTS = {
    system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    sans: "'Inter', 'Helvetica Neue', Arial, sans-serif",
    serif: "'Merriweather', Georgia, 'Times New Roman', serif",
    mono: "'JetBrains Mono', Menlo, Monaco, Consolas, monospace",
    heading: "'Playfair Display', Georgia, serif",
    display: "'Poppins', 'Segoe UI', sans-serif",
  };
  const SAMPLE = {
    name: "Drxlabs Diagnostics", tagline: "Trusted since 2004", address: "M4 Block, Vikashpuri, New Delhi",
    contact: "+91 98…", phone: "+91 98123 45678", email: "hello@yourlab.in", website: "yourlab.in",
    whatsapp: "+91 98123 45678", customer_care: "1800-DRX-LABS", toll_free: "1800-123-4567",
    emergency: "+91 98123 EMERG", working_hours: "Mon-Sat: 7am–9pm",
    license: "License: DL-2023-XXXXX", gst: "GST: 29ABCDE1234F1Z5", cin: "CIN: U85110DL2020PTC000000", registration: "Reg. No.: NABL M-0001",
    social: "@yourlab", timestamp: "Report generated: 2026-02-06", report_id: "Report ID: A1B2C3D4E5F6",
    custom: "Custom letterhead text", disclaimer: "For informational purposes only.",
  };
  return (
    <div className="pt-4 border-t border-border/60">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold">Per-field style overrides</div>
          <div className="text-[11px] text-ink/50 mt-0.5">Fine-tune each individual text field. Blank inputs inherit the global typography above.</div>
        </div>
      </div>
      <div className="space-y-6">
        {FIELD_GROUPS.map((group) => (
          <div key={group.title} data-testid={`fs-group-${group.title.toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-widest text-ink/40 font-semibold mb-2">{group.title}</div>
            <div className="grid gap-3">
              {Object.entries(group.keys).map(([key, label]) => {
                const s = styles[key] || {};
                const hasOverride = Boolean(s.size || s.weight || s.color || s.family);
                const previewStyle = {
                  fontFamily: FONTS[s.family || lh.font_family] || FONTS.system,
                  fontSize: `${s.size || lh.base_text_size || 11}px`,
                  color: s.color || lh.text_color || "#374151",
                  fontWeight: s.weight ?? lh.text_weight ?? 400,
                };
                return (
                  <div key={key} className={`rounded-xl border ${hasOverride ? "border-efgh-green/50 bg-efgh-green/[0.04]" : "border-border"} p-3`} data-testid={`fs-row-${key}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold text-ink">{label}</div>
                          {hasOverride && <span className="text-[10px] px-1.5 py-0.5 rounded bg-efgh-green/15 text-efgh-green font-medium">custom</span>}
                        </div>
                        <div className="mt-1 truncate" style={previewStyle} data-testid={`fs-preview-${key}`}>{SAMPLE[key] || label}</div>
                      </div>
                      {hasOverride && (
                        <button type="button" onClick={() => clearField(key)} className="text-[10px] text-ink/40 hover:text-danger whitespace-nowrap" data-testid={`fs-reset-${key}`}>Reset →</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-[10px]">Family</Label>
                        <select value={s.family || ""} onChange={(e) => setField(key, { family: e.target.value })} className="h-8 w-full rounded border border-border bg-white px-1 text-xs" data-testid={`fs-${key}-family`}>
                          <option value="">— inherit —</option>
                          <option value="system">System</option>
                          <option value="sans">Sans</option>
                          <option value="serif">Serif</option>
                          <option value="heading">Heading</option>
                          <option value="display">Display</option>
                          <option value="mono">Mono</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-[10px]">Size (px)</Label>
                        <Input type="number" min={7} max={40} value={s.size ?? ""} onChange={(e) => setField(key, { size: e.target.value ? parseInt(e.target.value, 10) : undefined })} className="h-8 text-xs" data-testid={`fs-${key}-size`} placeholder={String(lh.base_text_size || 11)} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Weight</Label>
                        <select value={s.weight ?? ""} onChange={(e) => setField(key, { weight: e.target.value ? parseInt(e.target.value, 10) : undefined })} className="h-8 w-full rounded border border-border bg-white px-1 text-xs" data-testid={`fs-${key}-weight`}>
                          <option value="">— inherit —</option>
                          <option value={300}>{"300 · Light"}</option>
                          <option value={400}>{"400 · Regular"}</option>
                          <option value={500}>{"500 · Medium"}</option>
                          <option value={600}>{"600 · Semibold"}</option>
                          <option value={700}>{"700 · Bold"}</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-[10px]">Colour</Label>
                        <div className="flex items-center gap-1.5">
                          <input type="color" value={s.color || lh.text_color || "#374151"} onChange={(e) => setField(key, { color: e.target.value })} className="w-8 h-8 rounded border border-border cursor-pointer" data-testid={`fs-${key}-color`} />
                          <div className="text-[10px] text-ink/50 font-mono truncate">{s.color || "inherit"}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LetterheadDesignCard({ form, setForm, onSave }) {
  const [saving, setSaving] = useState(false);
  const DEFAULTS = {
    logo_size: "md", primary_color: "#0B7A4D", accent_color: "#F4B400",
    border_top_thickness: 0, border_bottom_thickness: 1, border_color: "#0B7A4D",
    header_height_mm: 24, footer_height_mm: 16,
    header_left: "logo", header_center: "name_address", header_right: "contact",
    footer_left: "address", footer_right: "signature", footer_center: "none",
    custom_header_text: "", custom_footer_text: "",
    show_accreditation_logos: true, show_website_url: true, font_family: "system",
  };
  const lh = { ...DEFAULTS, ...(form.letterhead || {}) };
  const set = (k, v) => setForm({ ...form, letterhead: { ...lh, [k]: v } });
  const save = async () => { setSaving(true); try { await onSave(lh); } finally { setSaving(false); } };

  const HEADER_CHOICES = {
    logo: "Logo", name: "Lab name", name_address: "Name + Address",
    contact: "Contact (phone/email/web)",
    phone: "Phone / landline", email: "Email", website: "Website URL",
    whatsapp: "WhatsApp", customer_care: "Customer care", toll_free: "Toll-free",
    emergency: "Emergency contact", working_hours: "Working hours",
    accreditation: "Accreditation logos", custom_logos: "Custom / other logos",
    social: "Social handles",
    license: "Lab license no.", gst: "GST no.", cin: "CIN no.", registration: "Registration no.",
    qr: "QR code (link to report)",
    tagline: "Tagline / custom header text",
    none: "Empty",
  };
  const FOOTER_CHOICES = {
    address: "Address",
    contact: "Contact (phone/email/web)",
    phone: "Phone / landline", email: "Email", website: "Website URL",
    whatsapp: "WhatsApp", customer_care: "Customer care", toll_free: "Toll-free",
    emergency: "Emergency contact", working_hours: "Working hours",
    accreditation: "Accreditation logos", custom_logos: "Custom / other logos",
    social: "Social handles",
    license: "Lab license no.", gst: "GST no.", cin: "CIN no.", registration: "Registration no.",
    qr: "QR code (link to report)",
    signature: "Signature line", signatures: "Doctor signatures",
    timestamp: "Timestamp", report_id: "Report ID",
    disclaimer: "Disclaimer text",
    tagline: "Tagline",
    custom: "Custom text (from box below)",
    none: "Empty",
  };

  return (
    <Card className="p-0 rounded-2xl overflow-hidden" data-testid="letterhead-design-card">
      {/* Sticky header bar */}
      <div className="sticky top-0 z-10 px-6 py-4 bg-gradient-to-r from-ink/[0.03] to-transparent border-b border-border/60 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-efgh-orange" /> Letterhead design</div>
          <p className="text-sm text-ink/60">Layout · colours · typography · slots · borders</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-letterhead-design" className="bg-ink hover:bg-ink/90 text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save letterhead
        </Button>
      </div>

      <div className="p-6 space-y-6">
        {/* ============ 1. Letterhead Map — hero editor ============ */}
        <LhSection icon={LayoutTemplate} accent="text-efgh-blue" title="Letterhead map" subtitle="This 2×3 grid mirrors your printed page. Click any cell to add/re-order fields.">
          <div className="rounded-2xl border-2 border-dashed border-ink/25 p-3 bg-gradient-to-b from-muted/30 to-transparent" data-testid="letterhead-map">
            <div className="text-[9px] uppercase tracking-widest text-ink/40 text-center font-semibold pb-1.5">▲ Header ▲</div>
            <div className="grid grid-cols-3 gap-2">
              <SlotCell zone="Left"   value={lh.header_left}   choices={HEADER_CHOICES} onChange={(v) => set("header_left", v)}   testid="slot-header_left" />
              <SlotCell zone="Center" value={lh.header_center} choices={HEADER_CHOICES} onChange={(v) => set("header_center", v)} testid="slot-header_center" />
              <SlotCell zone="Right"  value={lh.header_right}  choices={HEADER_CHOICES} onChange={(v) => set("header_right", v)}  testid="slot-header_right" />
            </div>
            <div className="my-3 py-8 text-center text-[10px] uppercase tracking-widest text-ink/40 border-y border-dashed border-ink/20 bg-white/40">— Report body (Smart summary · biomarkers · lifestyle) —</div>
            <div className="text-[9px] uppercase tracking-widest text-ink/40 text-center font-semibold pb-1.5">▼ Footer ▼</div>
            <div className="grid grid-cols-3 gap-2">
              <SlotCell zone="Left"   value={lh.footer_left}   choices={FOOTER_CHOICES} onChange={(v) => set("footer_left", v)}   testid="slot-footer_left" />
              <SlotCell zone="Center" value={lh.footer_center} choices={FOOTER_CHOICES} onChange={(v) => set("footer_center", v)} testid="slot-footer_center" />
              <SlotCell zone="Right"  value={lh.footer_right}  choices={FOOTER_CHOICES} onChange={(v) => set("footer_right", v)}  testid="slot-footer_right" />
            </div>
          </div>
        </LhSection>

        {/* ============ 2. Brand colours & logo ============ */}
        <LhSection icon={Palette} accent="text-efgh-orange" title="Brand & logo" subtitle="Fine-tune where the lab logo appears on every report, its size, and its alignment.">
          <LogoPositionCard lh={lh} set={set} />
          <div className="mt-4 p-3 rounded-xl border border-border">
            <Label className="text-xs uppercase tracking-wider text-ink/60">Colours</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <ColorField label="Primary" value={lh.primary_color} onChange={(v) => set("primary_color", v)} testid="lh-primary" />
              <ColorField label="Accent"  value={lh.accent_color}  onChange={(v) => set("accent_color", v)}  testid="lh-accent" />
              <ColorField label="Border"  value={lh.border_color}  onChange={(v) => set("border_color", v)}  testid="lh-border-color" />
            </div>
            <p className="text-[10px] text-ink/40 mt-2">Border thickness &amp; placement (4 zones) live in the <b>Layout</b> tab.</p>
          </div>
        </LhSection>

        {/* ============ 3. Typography ============ */}
        <LhSection icon={PenLine} accent="text-efgh-green" title="Typography" subtitle="Global font settings for the letterhead (per-field overrides live on the Field styles tab).">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label>Font family</Label>
              <select value={lh.font_family || "system"} onChange={(e) => set("font_family", e.target.value)} data-testid="lh-font-family" className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm">
                <option value="system">System (default)</option>
                <option value="sans">Sans (Inter)</option>
                <option value="serif">Serif (Merriweather)</option>
                <option value="heading">Heading (Playfair)</option>
                <option value="display">Display (Poppins)</option>
                <option value="mono">Mono (JetBrains)</option>
              </select>
            </div>
            <div>
              <Label>Base size (px)</Label>
              <Input type="number" min={8} max={20} value={lh.base_text_size ?? 11} onChange={(e) => set("base_text_size", parseInt(e.target.value, 10) || 11)} data-testid="lh-base-size" />
            </div>
            <div>
              <Label>Colour</Label>
              <input type="color" value={lh.text_color || "#374151"} onChange={(e) => set("text_color", e.target.value)} data-testid="lh-text-color" className="w-full h-9 rounded cursor-pointer" />
            </div>
            <div>
              <Label>Weight</Label>
              <select value={lh.text_weight ?? 400} onChange={(e) => set("text_weight", parseInt(e.target.value, 10))} data-testid="lh-text-weight" className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm">
                <option value={300}>{"Light (300)"}</option>
                <option value={400}>{"Regular (400)"}</option>
                <option value={500}>{"Medium (500)"}</option>
                <option value={600}>{"Semibold (600)"}</option>
                <option value={700}>{"Bold (700)"}</option>
              </select>
            </div>
          </div>
        </LhSection>

        {/* ============ 4. Icons & QR ============ */}
        <LhSection icon={Award} accent="text-efgh-yellow" title="Icons & QR code" subtitle="Style for the little icons in front of phone/email/address and QR block colour.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Icon colour</Label>
              <input type="color" value={lh.icon_color || "#0B7A4D"} onChange={(e) => set("icon_color", e.target.value)} data-testid="lh-icon-color" className="w-full h-9 rounded cursor-pointer" />
            </div>
            <div>
              <Label>Icon size (px)</Label>
              <Input type="number" min={8} max={24} value={lh.icon_size ?? 12} onChange={(e) => set("icon_size", parseInt(e.target.value, 10) || 12)} data-testid="lh-icon-size" />
            </div>
            <div>
              <Label>QR code colour</Label>
              <div className="flex gap-1 items-center">
                <input type="color" value={lh.qr_color || lh.icon_color || "#0B7A4D"} onChange={(e) => set("qr_color", e.target.value)} data-testid="lh-qr-color" className="w-full h-9 rounded cursor-pointer" />
                {lh.qr_color && <button type="button" onClick={() => set("qr_color", "")} className="text-[10px] text-ink/40 hover:text-ink whitespace-nowrap" title="Reset to follow icon colour">reset</button>}
              </div>
              <p className="text-[10px] text-ink/40 mt-0.5">Blank → follow icon colour.</p>
            </div>
          </div>
        </LhSection>

        {/* ============ 5. Page sizing ============ */}
        <LhSection icon={LayoutTemplate} accent="text-efgh-blue" title="Header & footer heights" subtitle="Vertical space reserved for header and footer on every printed page.">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Header height (mm)</Label>
              <Input type="number" min={10} max={80} value={lh.header_height_mm ?? 24} onChange={(e) => set("header_height_mm", parseInt(e.target.value, 10) || 24)} data-testid="lh-header-height" />
            </div>
            <div>
              <Label>Footer height (mm)</Label>
              <Input type="number" min={8} max={60} value={lh.footer_height_mm ?? 16} onChange={(e) => set("footer_height_mm", parseInt(e.target.value, 10) || 16)} data-testid="lh-footer-height" />
            </div>
          </div>
        </LhSection>

        {/* ============ 6. Custom text & toggles ============ */}
        <LhSection icon={PenLine} accent="text-efgh-orange" title="Custom text & visibility" subtitle="Free-form taglines for header/footer slots and quick visibility toggles.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Custom header text (tagline slot)</Label>
              <Input value={lh.custom_header_text || ""} onChange={(e) => set("custom_header_text", e.target.value)} placeholder="e.g. NABL Accredited · Trusted since 2001" data-testid="lh-header-text" />
            </div>
            <div>
              <Label>Custom footer text</Label>
              <Input value={lh.custom_footer_text || ""} onChange={(e) => set("custom_footer_text", e.target.value)} placeholder="e.g. This report is not for medico-legal use." data-testid="lh-footer-text" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-3">
            <ToggleChip checked={!!lh.show_accreditation_logos} onChange={(v) => set("show_accreditation_logos", v)} label="Show accreditation logos in header" testid="lh-show-accred" />
            <ToggleChip checked={!!lh.show_website_url} onChange={(v) => set("show_website_url", v)} label="Show website URL" testid="lh-show-web" />
          </div>
        </LhSection>

        {/* ============ Backgrounds + Secondary rows ============ */}
        <LhSection icon={Palette} accent="text-efgh-orange" title="Backgrounds & secondary rows" subtitle="Give the header and footer their own background colours, or add a second row for extra branding (secondary header sits below the primary header; secondary footer sits above the primary footer).">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ColorField label="Header BG"   value={lh.header_bg_color   || "#ffffff"} onChange={(v) => set("header_bg_color", v)}   testid="lh-header-bg" />
            <ColorField label="Footer BG"   value={lh.footer_bg_color   || "#ffffff"} onChange={(v) => set("footer_bg_color", v)}   testid="lh-footer-bg" />
            <ColorField label="Sec header BG" value={lh.sec_header_bg_color || lh.header_bg_color || "#ffffff"} onChange={(v) => set("sec_header_bg_color", v)} testid="lh-sec-header-bg" />
            <ColorField label="Sec footer BG" value={lh.sec_footer_bg_color || lh.footer_bg_color || "#ffffff"} onChange={(v) => set("sec_footer_bg_color", v)} testid="lh-sec-footer-bg" />
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <ToggleChip checked={!!lh.secondary_header_enabled} onChange={(v) => set("secondary_header_enabled", v)} label="Enable secondary header (below primary)" testid="lh-sec-header-enable" />
            <ToggleChip checked={!!lh.secondary_footer_enabled} onChange={(v) => set("secondary_footer_enabled", v)} label="Enable secondary footer (above primary)" testid="lh-sec-footer-enable" />
          </div>
          {lh.secondary_header_enabled && (
            <div className="mt-4 p-3 rounded-lg border border-dashed border-efgh-blue/40 bg-efgh-blue/[0.03]">
              <div className="text-[11px] uppercase tracking-wider text-efgh-blue mb-2">Secondary header slots</div>
              <div className="grid sm:grid-cols-3 gap-2">
                <SlotCell zone="Left"   value={lh.sec_header_left}   choices={HEADER_CHOICES} onChange={(v) => set("sec_header_left", v)}   testid="slot-sec_header_left" />
                <SlotCell zone="Center" value={lh.sec_header_center} choices={HEADER_CHOICES} onChange={(v) => set("sec_header_center", v)} testid="slot-sec_header_center" />
                <SlotCell zone="Right"  value={lh.sec_header_right}  choices={HEADER_CHOICES} onChange={(v) => set("sec_header_right", v)}  testid="slot-sec_header_right" />
              </div>
              <SlotAlignmentRow keys={["sec_header_left","sec_header_center","sec_header_right"]} lh={lh} set={set} />
            </div>
          )}
          {lh.secondary_footer_enabled && (
            <div className="mt-4 p-3 rounded-lg border border-dashed border-efgh-green/40 bg-efgh-green/[0.03]">
              <div className="text-[11px] uppercase tracking-wider text-efgh-green mb-2">Secondary footer slots</div>
              <div className="grid sm:grid-cols-3 gap-2">
                <SlotCell zone="Left"   value={lh.sec_footer_left}   choices={FOOTER_CHOICES} onChange={(v) => set("sec_footer_left", v)}   testid="slot-sec_footer_left" />
                <SlotCell zone="Center" value={lh.sec_footer_center} choices={FOOTER_CHOICES} onChange={(v) => set("sec_footer_center", v)} testid="slot-sec_footer_center" />
                <SlotCell zone="Right"  value={lh.sec_footer_right}  choices={FOOTER_CHOICES} onChange={(v) => set("sec_footer_right", v)}  testid="slot-sec_footer_right" />
              </div>
              <SlotAlignmentRow keys={["sec_footer_left","sec_footer_center","sec_footer_right"]} lh={lh} set={set} />
            </div>
          )}
          <div className="mt-4 p-3 rounded-lg border border-border bg-muted/30">
            <div className="text-[11px] uppercase tracking-wider text-ink/60 mb-2">Alignment within each slot</div>
            <p className="text-[10px] text-ink/40 mb-2">Controls how multiple fields inside the same slot stack (left/center/right justify).</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <SlotAlignmentRow label="Header row" keys={["header_left","header_center","header_right"]} lh={lh} set={set} />
              <SlotAlignmentRow label="Footer row" keys={["footer_left","footer_center","footer_right"]} lh={lh} set={set} />
            </div>
          </div>
        </LhSection>
      </div>
    </Card>
  );
}

/* ================= Sub-components for LetterheadDesignCard UI ================= */
function LhSection({ icon: Icon, accent = "text-ink", title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-border bg-white p-4 md:p-5" data-testid={`lh-section-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`shrink-0 w-8 h-8 rounded-lg bg-muted/60 grid place-items-center ${accent}`}><Icon className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-semibold text-ink text-sm">{title}</div>
          {subtitle && <div className="text-[11px] text-ink/50 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {children}
    </section>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="flex flex-col items-stretch min-w-0">
      <span className="text-[10px] text-ink/60 mb-1">{label}</span>
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full h-9 rounded cursor-pointer border border-border" />
      <span className="text-[9px] text-ink/40 font-mono mt-0.5 truncate">{value}</span>
    </div>
  );
}

/**
 * LogoPositionCard — one-stop logo customization for lab admins.
 * Controls:
 *   • Logo height (slider 24-96px), overrides the legacy sm/md/lg presets
 *   • Alignment within its slot (left / center / right)
 *   • Header on/off + Footer on/off toggles (auto-toggles the slot value)
 *   • Header/Footer position (Left / Center / Right)
 *
 * Backwards-compat: if `logo_height_px` is unset we fall back to the legacy
 * sm/md/lg preset. Setting a slider value writes `logo_height_px` and clears
 * `logo_size` on save so the numeric value wins everywhere.
 */
function LogoPositionCard({ lh, set }) {
  const heightPx = Number.isFinite(lh.logo_height_px)
    ? lh.logo_height_px
    : ({ sm: 32, md: 56, lg: 76 }[lh.logo_size] || 56);
  const align = lh.logo_align || "left";
  const headerSlots = ["header_left", "header_center", "header_right"];
  const footerSlots = ["footer_left", "footer_center", "footer_right"];
  // A slot value is either a string ("logo" | "none" | ...) or an array of
  // choices (multi-select). Support both formats.
  const slotHasLogo = (v) => Array.isArray(v) ? v.includes("logo") : v === "logo";
  const removeLogoFromSlot = (v) => {
    if (Array.isArray(v)) {
      const cleaned = v.filter((x) => x !== "logo");
      return cleaned.length ? (cleaned.length === 1 ? cleaned[0] : cleaned) : "none";
    }
    return "none";
  };
  const addLogoToSlot = (v) => {
    if (Array.isArray(v)) return v.includes("logo") ? v : [...v, "logo"];
    if (!v || v === "none") return "logo";
    return "logo" === v ? v : [v, "logo"];
  };
  const headerSlot = headerSlots.find((k) => slotHasLogo(lh[k])) || null;
  const footerSlot = footerSlots.find((k) => slotHasLogo(lh[k])) || null;
  const inHeader = !!headerSlot;
  const inFooter = !!footerSlot;

  // Toggle logo on/off in a given band. When turning ON, place in the currently-selected
  // position (or fall back to the first slot that's empty/none). When turning OFF, remove
  // "logo" from whichever slot currently has it.
  const toggleBand = (band, on) => {
    const slots = band === "header" ? headerSlots : footerSlots;
    const currentSlot = band === "header" ? headerSlot : footerSlot;
    if (on) {
      if (currentSlot) return;
      const target = slots.find((k) => !lh[k] || lh[k] === "none" || (Array.isArray(lh[k]) && lh[k].length === 0)) || slots[0];
      set(target, addLogoToSlot(lh[target]));
    } else if (currentSlot) {
      set(currentSlot, removeLogoFromSlot(lh[currentSlot]));
    }
  };

  // Move logo to a specific slot inside its band.
  const moveTo = (band, side) => {
    const slots = band === "header" ? headerSlots : footerSlots;
    const target = `${band}_${side}`;
    if (!slots.includes(target)) return;
    // Strip logo from every other slot in this band first.
    slots.forEach((k) => {
      if (k !== target && slotHasLogo(lh[k])) {
        set(k, removeLogoFromSlot(lh[k]));
      }
    });
    set(target, addLogoToSlot(lh[target]));
  };

  const positionOf = (band) => {
    const slot = band === "header" ? headerSlot : footerSlot;
    if (!slot) return null;
    return slot.split("_")[1]; // left | center | right
  };

  return (
    <div className="p-4 rounded-2xl border border-border bg-gradient-to-br from-white to-slate-50/60" data-testid="logo-position-card">
      <div className="flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-efgh-orange" />
        <div className="font-heading font-semibold text-ink text-sm">Logo placement &amp; size</div>
      </div>
      <p className="text-[11px] text-ink/50 mt-0.5">Show your lab logo on the header, footer, or both — with pixel-perfect size control.</p>

      {/* Size slider */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-xs uppercase tracking-wider text-ink/60">Logo height</Label>
          <span className="text-xs font-mono text-ink/70" data-testid="logo-height-value">{heightPx}px</span>
        </div>
        <Slider
          min={24}
          max={96}
          step={2}
          value={[heightPx]}
          onValueChange={(v) => set("logo_height_px", v[0])}
          data-testid="logo-height-slider"
        />
        <div className="flex justify-between text-[9px] text-ink/40 mt-1 uppercase tracking-wider">
          <span>Compact 24px</span><span>Medium 56px</span><span>Prominent 96px</span>
        </div>
      </div>

      {/* Alignment */}
      <div className="mt-5">
        <Label className="text-xs uppercase tracking-wider text-ink/60">Alignment within slot</Label>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {[
            { id: "left",   label: "Left",   Icon: AlignLeft },
            { id: "center", label: "Center", Icon: AlignCenter },
            { id: "right",  label: "Right",  Icon: AlignRight },
          ].map(({ id, label, Icon }) => {
            const active = align === id;
            return (
              <button
                key={id}
                type="button"
                data-testid={`logo-align-${id}`}
                onClick={() => set("logo_align", id)}
                className={`h-11 rounded-xl border transition-all flex items-center justify-center gap-1.5 ${active ? "bg-ink text-white border-ink shadow-sm" : "border-border text-ink/70 hover:bg-muted"}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Header on/off + position */}
      <div className="mt-5 space-y-3">
        <LogoBandControl
          band="header"
          label="Show logo on header"
          active={inHeader}
          position={positionOf("header")}
          onToggle={(on) => toggleBand("header", on)}
          onMove={(side) => moveTo("header", side)}
        />
        <LogoBandControl
          band="footer"
          label="Show logo on footer"
          active={inFooter}
          position={positionOf("footer")}
          onToggle={(on) => toggleBand("footer", on)}
          onMove={(side) => moveTo("footer", side)}
        />
        <p className="text-[10.5px] text-ink/40">Tip: showing the logo in both bands doubles brand recall on multi-page reports.</p>
      </div>
    </div>
  );
}

function LogoBandControl({ band, label, active, position, onToggle, onMove }) {
  return (
    <div className={`rounded-xl border transition-colors ${active ? "border-efgh-green/40 bg-efgh-green/[0.04]" : "border-border bg-white"}`}>
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Switch
            checked={active}
            onCheckedChange={onToggle}
            data-testid={`logo-in-${band}-toggle`}
          />
          <Label className="text-sm text-ink cursor-pointer" onClick={() => onToggle(!active)}>{label}</Label>
        </div>
        {active && (
          <div className="flex gap-1" data-testid={`logo-${band}-position`}>
            {[
              { id: "left",   Icon: AlignLeft },
              { id: "center", Icon: AlignCenter },
              { id: "right",  Icon: AlignRight },
            ].map(({ id, Icon }) => {
              const on = position === id;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`logo-${band}-${id}`}
                  onClick={() => onMove(id)}
                  className={`w-8 h-8 rounded-lg border transition-all grid place-items-center ${on ? "bg-ink text-white border-ink" : "border-border text-ink/60 hover:bg-muted"}`}
                  aria-label={`Move logo to ${id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


function ToggleChip({ checked, onChange, label, testid }) {
  return (
    <label className={`inline-flex items-center gap-2 h-9 pl-2 pr-3 rounded-full border cursor-pointer transition-colors ${checked ? "border-efgh-green/50 bg-efgh-green/[0.08]" : "border-border bg-white hover:bg-muted"}`}>
      <input type="checkbox" className="w-4 h-4" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span className="text-xs text-ink/80">{label}</span>
    </label>
  );
}

function SlotChipPicker({ label, value, choices, onChange, testid }) {
  // Normalise legacy string value into an array — we now store multi-picks per slot.
  const arr = Array.isArray(value) ? value : (value && value !== "none" ? [value] : []);
  const [adding, setAdding] = useState("");
  const add = () => {
    if (!adding) return;
    if (arr.includes(adding)) { setAdding(""); return; }
    onChange([...arr, adding]);
    setAdding("");
  };
  const removeAt = (i) => {
    const next = arr.filter((_, idx) => idx !== i);
    onChange(next.length === 0 ? ["none"] : next);
  };
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...arr];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };
  const available = Object.entries(choices).filter(([k]) => !arr.includes(k) && k !== "none");
  return (
    <div className="grid grid-cols-[100px_1fr] items-start gap-2" data-testid={testid}>
      <Label className="capitalize pt-2">{label}</Label>
      <div className="min-w-0">
        <div className="flex flex-wrap gap-1.5 min-h-[32px] p-1.5 rounded-md border border-border bg-muted/30">
          {arr.length === 0 && <span className="text-[11px] text-ink/40 italic px-1 py-0.5">Empty — add a field →</span>}
          {arr.map((c, i) => (
            <span key={`${c}-${i}`} className="inline-flex items-center gap-1 bg-white border border-border rounded-full pl-2 pr-1 py-0.5 text-[11px]" data-testid={`${testid}-chip-${i}`}>
              {i > 0 && <button type="button" onClick={() => moveUp(i)} title="Move up" className="text-ink/40 hover:text-ink" data-testid={`${testid}-up-${i}`}>↑</button>}
              <span className="text-ink/80">{choices[c] || c}</span>
              <button type="button" onClick={() => removeAt(i)} className="w-4 h-4 rounded-full hover:bg-danger/10 text-ink/40 hover:text-danger grid place-items-center" data-testid={`${testid}-remove-${i}`}>×</button>
            </span>
          ))}
        </div>
        {available.length > 0 && (
          <div className="mt-1.5 flex gap-1.5">
            <select value={adding} onChange={(e) => setAdding(e.target.value)} className="h-8 rounded-md border border-border bg-white px-2 text-xs flex-1 min-w-0" data-testid={`${testid}-select`}>
              <option value="">Add a field…</option>
              {available.map(([kk, vv]) => <option key={kk} value={kk}>{vv}</option>)}
            </select>
            <Button type="button" size="sm" variant="outline" onClick={add} disabled={!adding} data-testid={`${testid}-add`} className="h-8 text-xs px-3"><Plus className="w-3 h-3 mr-1" />Add</Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * SlotAlignmentRow — per-slot horizontal alignment picker.
 * Renders 3 compact select controls (one per slot key) that let the lab admin
 * choose start / center / end alignment for content stacked inside each slot.
 * All values live under `letterhead.slot_align.<slot_key>`.
 */
function SlotAlignmentRow({ label, keys, lh, set }) {
  const align = lh.slot_align || {};
  const patch = (k, v) => {
    const next = { ...(lh.slot_align || {}), [k]: v };
    set("slot_align", next);
  };
  return (
    <div className="mt-3">
      {label && <div className="text-[10px] uppercase tracking-wider text-ink/50 mb-1">{label} · alignment</div>}
      <div className="grid grid-cols-3 gap-2">
        {keys.map((k) => {
          const short = k.replace(/^sec_/, "").split("_").pop();
          return (
            <div key={k} className="flex items-center gap-1">
              <span className="text-[10px] text-ink/50 capitalize w-12 shrink-0">{short}</span>
              <select
                value={align[k] || "start"}
                onChange={(e) => patch(k, e.target.value)}
                data-testid={`slot-align-${k}`}
                className="h-7 rounded-md border border-border bg-white px-1 text-[11px] flex-1 min-w-0"
              >
                <option value="start">Left</option>
                <option value="center">Center</option>
                <option value="end">Right</option>
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * SlotCell — vertical variant used inside the visual 2×3 letterhead map.
 * Each cell shows its zone name at the top, then stacked chips, then an "Add field" row at the bottom.
 * Full-width inside the map cell so admins can see every configured field at a glance.
 */
function SlotCell({ zone, value, choices, onChange, testid }) {
  const arr = Array.isArray(value) ? value : (value && value !== "none" ? [value] : []);
  const [adding, setAdding] = useState("");
  const add = () => {
    if (!adding) return;
    if (arr.includes(adding)) { setAdding(""); return; }
    onChange([...arr, adding]);
    setAdding("");
  };
  const removeAt = (i) => {
    const next = arr.filter((_, idx) => idx !== i);
    onChange(next.length === 0 ? ["none"] : next);
  };
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...arr];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };
  const moveDown = (i) => {
    if (i === arr.length - 1) return;
    const next = [...arr];
    [next[i + 1], next[i]] = [next[i], next[i + 1]];
    onChange(next);
  };
  const available = Object.entries(choices).filter(([k]) => !arr.includes(k) && k !== "none");
  return (
    <div className="rounded-lg border border-border bg-white p-2 flex flex-col min-h-[140px] shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-ink/60 font-bold mb-1.5 flex items-center justify-between">
        <span>{zone}</span>
        {arr.length > 0 && <span className="text-[9px] px-1.5 rounded-full bg-efgh-green/15 text-efgh-green normal-case tracking-normal font-semibold">{arr.length}</span>}
      </div>
      <div className="flex-1 space-y-1 min-h-[40px]">
        {arr.length === 0 && <div className="text-[10px] text-ink/40 italic border border-dashed border-border rounded p-2 text-center">Empty — add a field ↓</div>}
        {arr.map((c, i) => (
          <div key={`${c}-${i}`} className="flex items-center gap-1 bg-muted/40 border border-border rounded px-1.5 py-0.5" data-testid={`${testid}-chip-${i}`}>
            <span className="text-[11px] text-ink/80 flex-1 truncate">{choices[c] || c}</span>
            <div className="flex items-center opacity-70">
              {i > 0 && <button type="button" onClick={() => moveUp(i)} title="Move up" className="w-4 h-4 text-ink/50 hover:text-ink text-[10px]" data-testid={`${testid}-up-${i}`}>↑</button>}
              {i < arr.length - 1 && <button type="button" onClick={() => moveDown(i)} title="Move down" className="w-4 h-4 text-ink/50 hover:text-ink text-[10px]" data-testid={`${testid}-down-${i}`}>↓</button>}
              <button type="button" onClick={() => removeAt(i)} className="w-4 h-4 rounded-full hover:bg-danger/10 text-ink/50 hover:text-danger grid place-items-center text-[10px]" data-testid={`${testid}-remove-${i}`}>×</button>
            </div>
          </div>
        ))}
      </div>
      {available.length > 0 && (
        <div className="mt-1.5 flex gap-1">
          <select value={adding} onChange={(e) => setAdding(e.target.value)} className="h-7 rounded border border-border bg-white px-1 text-[11px] flex-1 min-w-0" data-testid={`${testid}-select`}>
            <option value="">+ field</option>
            {available.map(([kk, vv]) => <option key={kk} value={kk}>{vv}</option>)}
          </select>
          <button type="button" onClick={add} disabled={!adding} data-testid={`${testid}-add`} className="h-7 px-2 rounded border border-border text-[11px] hover:bg-muted disabled:opacity-40"><Plus className="w-3 h-3" /></button>
        </div>
      )}
    </div>
  );
}

function LetterheadPreview({ lab }) {
  const lh = { logo_size: "md", primary_color: "#0B7A4D", accent_color: "#F4B400", border_top_thickness: 0, border_bottom_thickness: 1, border_color: "#0B7A4D", header_height_mm: 24, footer_height_mm: 16, header_left: "logo", header_center: "name_address", header_right: "contact", footer_left: "address", footer_center: "none", footer_right: "signature", show_accreditation_logos: true, show_website_url: true, custom_header_text: "", custom_footer_text: "", font_family: "system", text_color: "#374151", base_text_size: 11, text_weight: 400, icon_color: "#0B7A4D", icon_size: 12, ...(lab?.letterhead || {}) };
  const FONTS = {
    system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    sans: "'Inter', 'Helvetica Neue', Arial, sans-serif",
    serif: "'Merriweather', Georgia, 'Times New Roman', serif",
    mono: "'JetBrains Mono', Menlo, Monaco, Consolas, monospace",
    heading: "'Playfair Display', Georgia, serif",
    display: "'Poppins', 'Segoe UI', sans-serif",
  };
  const previewStyle = {
    fontFamily: FONTS[lh.font_family] || FONTS.system,
    color: lh.text_color || "#374151",
    fontWeight: lh.text_weight ?? 400,
  };
  const logoHeight = Number.isFinite(lh.logo_height_px)
    ? lh.logo_height_px
    : ({ sm: 32, md: 56, lg: 76 }[lh.logo_size] || 56);
  const logoAlignClass = { left: "justify-start", center: "justify-center", right: "justify-end" }[lh.logo_align || "left"];
  const logoImg = lab.logo_url
    ? <img src={lab.logo_url} alt="" className="object-contain" style={{ height: `${logoHeight}px`, maxWidth: `${logoHeight * 3.2}px` }} />
    : <div className="w-10 h-10 rounded-lg grid place-items-center text-white font-bold" style={{ background: lh.primary_color }}>{(lab.name || "L")[0]}</div>;
  const slot = (choice, isFooter = false) => {
    switch (choice) {
      case "logo":
        return <div className={`flex items-center w-full ${logoAlignClass}`}>{logoImg}</div>;
      case "name":
        return <div className="font-heading font-bold text-lg" style={{ color: lh.primary_color }}>{lab.name || "Your Lab"}</div>;
      case "name_address":
        return (<div><div className="font-heading font-bold text-lg" style={{ color: lh.primary_color }}>{lab.name || "Your Lab"}</div>{lab.tagline && <div className="text-[10px] text-ink/60">{lab.tagline}</div>}{lab.address && <div className="text-[10px] text-ink/70 mt-0.5">{lab.address}{lab.city ? `, ${lab.city}` : ""}</div>}</div>);
      case "contact":
        return (<div className="text-[10px] text-ink/70 text-right">{lab.phone && <div>📞 {lab.phone}</div>}{lab.email && <div>✉ {lab.email}</div>}{lh.show_website_url && lab.website && <div>🌐 {lab.website.replace(/^https?:\/\//, "")}</div>}</div>);
      case "accreditation":
        return <div className="text-[10px] text-ink/50">Accreditation logos</div>;
      case "custom_logos":
        return (lab.custom_logos || []).length > 0 ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            {(lab.custom_logos || []).map((c, i) => <img key={c.id || i} src={c.logo_url} alt={c.label} title={c.label} className="h-6 max-w-[52px] object-contain" />)}
          </div>
        ) : <div className="text-[10px] text-ink/40 italic">Custom logos (upload above)</div>;
      case "tagline":
        return <div className="text-[10px] italic" style={{ color: lh.accent_color }}>{lh.custom_header_text || lab.tagline || "Trusted lab"}</div>;
      case "address":
        return <div className="text-[9px] text-ink/60">{lab.address}{lab.city ? `, ${lab.city}` : ""}</div>;
      case "license":
        return <div className="text-[9px] text-ink/60">License: {lab.license_no || "—"}</div>;
      case "signature":
        return <div className="text-[9px] text-ink/60 text-right">For {lab.name || "Your Lab"}<div className="mt-2 pt-0.5 border-t border-ink/30 w-24 ml-auto">Authorised Signatory</div></div>;
      case "signatures":
        return <div className="text-[9px] text-ink/60">Doctor signature(s) will appear here on the report.</div>;
      case "timestamp":
        return <div className="text-[9px] text-ink/60">Report date: {new Date().toISOString().slice(0, 10)}</div>;
      case "custom":
        return <div className="text-[9px] text-ink/60">{isFooter ? lh.custom_footer_text : lh.custom_header_text}</div>;
      case "phone": return lab.phone ? <div className="text-[9px] text-ink/70">📞 {lab.phone}</div> : null;
      case "email": return lab.email ? <div className="text-[9px] text-ink/70">✉ {lab.email}</div> : null;
      case "website": return lab.website ? <div className="text-[9px] text-ink/70">🌐 {lab.website.replace(/^https?:\/\//, "")}</div> : null;
      case "whatsapp": return lab.whatsapp_number ? <div className="text-[9px] text-ink/70">💬 {lab.whatsapp_number}</div> : null;
      case "customer_care": return lab.customer_care ? <div className="text-[9px] text-ink/70">☎ {lab.customer_care}</div> : null;
      case "toll_free": return lab.toll_free ? <div className="text-[9px] text-ink/70">Toll-free: {lab.toll_free}</div> : null;
      case "emergency": return lab.emergency_contact ? <div className="text-[9px] text-danger/80">🚨 {lab.emergency_contact}</div> : null;
      case "working_hours": return lab.working_hours ? <div className="text-[9px] text-ink/70">🕒 {lab.working_hours}</div> : null;
      case "gst": return lab.gst_number ? <div className="text-[9px] text-ink/60">GST: {lab.gst_number}</div> : null;
      case "cin": return lab.cin_number ? <div className="text-[9px] text-ink/60">CIN: {lab.cin_number}</div> : null;
      case "registration": return lab.registration_no ? <div className="text-[9px] text-ink/60">Reg: {lab.registration_no}</div> : null;
      case "social":
        return (lab.social_handles || []).length > 0 ? (
          <div className="text-[9px] text-ink/70 flex flex-wrap gap-1.5">{(lab.social_handles || []).map((s, i) => <span key={i}>{s.handle || s.url || s.platform}</span>)}</div>
        ) : null;
      case "qr":
        return <div className="text-[9px] text-ink/40 italic border border-dashed rounded p-1">[QR code — appears on report]</div>;
      case "report_id":
        return <div className="text-[9px] text-ink/60">Report ID</div>;
      case "disclaimer":
        return <div className="text-[8px] text-ink/50 italic">For informational purposes only.</div>;
      default:
        return null;
    }
  };
  const slotStack = (value, isFooter = false) => {
    const arr = Array.isArray(value) ? value : (value && value !== "none" ? [value] : []);
    if (arr.length === 0) return null;
    return <div className="space-y-1">{arr.map((c, i) => <div key={`${c}-${i}`}>{slot(c, isFooter)}</div>)}</div>;
  };
  const anyFooter = ["footer_left", "footer_center", "footer_right"].some((k) => {
    const v = lh[k]; const arr = Array.isArray(v) ? v : (v && v !== "none" ? [v] : []); return arr.length > 0;
  });
  return (
    <div className="mt-4">
      <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold mb-2">Live preview</div>
      <div
        className="bg-white shadow-sm"
        data-testid="letterhead-preview"
        style={{ borderTop: lh.border_top_thickness ? `${lh.border_top_thickness}px solid ${lh.border_color}` : "none", borderBottom: lh.border_bottom_thickness ? `${lh.border_bottom_thickness}px solid ${lh.border_color}` : "none", maxWidth: "560px", ...previewStyle }}
      >
        <div className="grid grid-cols-[auto_1fr_auto] gap-4 items-start p-4">
          <div>{slotStack(lh.header_left)}</div>
          <div className="min-w-0">{slotStack(lh.header_center)}</div>
          <div>{slotStack(lh.header_right)}</div>
        </div>
        <div className="px-4 pb-4">
          <div className="text-[10px] text-ink/40 italic">— Report content will appear here (Patient details, Health Score, Smart Summary…) —</div>
        </div>
        {anyFooter && (
          <div className="px-4 pt-2 pb-3 border-t border-border/50 grid grid-cols-3 gap-3 items-end">
            <div>{slotStack(lh.footer_left, true)}</div>
            <div className="text-center">{slotStack(lh.footer_center, true)}</div>
            <div className="text-right">{slotStack(lh.footer_right, true)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function LetterheadMargins({ form, onSave }) {
  const [top, setTop] = useState(form.letterhead_content_top_mm ?? 40);
  const [bottom, setBottom] = useState(form.letterhead_content_bottom_mm ?? 20);
  useEffect(() => {
    setTop(form.letterhead_content_top_mm ?? 40);
    setBottom(form.letterhead_content_bottom_mm ?? 20);
  }, [form.letterhead_content_top_mm, form.letterhead_content_bottom_mm]);

  return (
    <div className="w-full mt-2 pt-4 border-t border-border" data-testid="letterhead-margins">
      <div className="text-[11px] uppercase tracking-widest text-ink/50 font-semibold">Content area (where the report text sits on your letterhead)</div>
      <p className="text-xs text-ink/60 mt-1">Set the top and bottom space your letterhead&apos;s header/footer occupies. Report content flows only inside this blank area on every printed page.</p>
      <div className="mt-3 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div>
          <Label className="flex items-center justify-between">Top margin <span className="text-xs text-ink/40">{top}mm</span></Label>
          <Input
            type="number" min={0} max={120} step={1}
            data-testid="letterhead-margin-top"
            value={top}
            onChange={(e) => setTop(parseInt(e.target.value, 10) || 0)}
          />
        </div>
        <div>
          <Label className="flex items-center justify-between">Bottom margin <span className="text-xs text-ink/40">{bottom}mm</span></Label>
          <Input
            type="number" min={0} max={80} step={1}
            data-testid="letterhead-margin-bottom"
            value={bottom}
            onChange={(e) => setBottom(parseInt(e.target.value, 10) || 0)}
          />
        </div>
        <Button
          type="button"
          data-testid="save-letterhead-margins"
          onClick={() => onSave({ top, bottom })}
          className="bg-ink hover:bg-ink/90 text-white h-10"
        >
          Save margins
        </Button>
      </div>
      {/* Mini A4 preview */}
      <div className="mt-4 flex items-start gap-3">
        <div className="w-[140px] shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-ink/40 font-semibold mb-1">Preview (A4)</div>
          <div className="relative bg-white border border-border rounded" style={{ width: 140, height: 198 /* A4 aspect */ }}>
            {form.custom_letterhead_url && (
              <img src={form.custom_letterhead_url} alt="" className="absolute inset-0 w-full h-full object-contain" />
            )}
            <div
              className="absolute left-1.5 right-1.5 border border-dashed border-efgh-green bg-efgh-green/10"
              style={{
                top: `${Math.min(100, (top / 297) * 100)}%`,
                bottom: `${Math.min(100, (bottom / 297) * 100)}%`,
              }}
            >
              <div className="absolute inset-0 grid place-items-center text-[9px] text-efgh-green font-semibold uppercase tracking-wider">Report content</div>
            </div>
          </div>
        </div>
        <div className="flex-1 text-xs text-ink/60 space-y-2 pt-4">
          <div><b>Tip:</b> Print your letterhead template, measure the blank area with a ruler, and enter those values here.</div>
          <div><b>Common values:</b> Header logo ≈ 30-45mm · Footer signature/address ≈ 15-25mm.</div>
        </div>
      </div>
    </div>
  );
}
